import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { Temporal } from "@js-temporal/polyfill";
import { Avatar, Button, Progress, ScrollShadow, Tooltip } from "@heroui/react";
import {
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  PauseIcon,
  StopIcon,
} from "@heroicons/react/24/outline";
import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";
import { HeroIcon } from "../HeroIcon";
import MainStore from "../MainStore";
import useDataEvent from "../CustomHook";
import PrimitiveConfig from "../PrimitiveConfig";
import useWorkspaceBranding from "../hooks/useWorkspaceBranding";
import { collectPausedVerificationViews } from "./pausedVerificationPlugins";
import { OUTPUT_ENTITY_TYPES, groupPrimitivesByType } from "../utils/flowEvidence";

function formatNumber(value) {
  if (value == null || Number.isNaN(value)) {
    return undefined;
  }
  if (typeof value !== "number") {
    const parsed = Number(value);
    if (Number.isNaN(parsed)) {
      return undefined;
    }
    value = parsed;
  }

  if (value >= 1000000) {
    return `${(value / 1000000).toFixed(1).replace(/\.0$/, "")}M`;
  }
  if (value >= 1000) {
    return `${(value / 1000).toFixed(1).replace(/\.0$/, "")}k`;
  }
  return `${Math.round(value)}`;
}

const usdFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 4,
});

const REMOTE_PREVIEW_REFERENCE_IDS = new Set([34, 78]);
const PROCESSING_STEP_TYPES = new Set(["categorizer", "query", "search", "summary"]);
const PROGRESS_CANDIDATE_TYPES = new Set(["query", "summary", "search", "categorizer"]);
function safePercent(value) {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return undefined;
  }
  let percent = value;
  if (percent <= 1) {
    percent *= 100;
  }
  if (!Number.isFinite(percent)) {
    return undefined;
  }
  if (percent < 0) {
    return 0;
  }
  if (percent > 100) {
    return 100;
  }
  return percent;
}

function toUnitProgress(value) {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return undefined;
  }
  if (value < 0) {
    return 0;
  }
  return value > 1 ? Math.min(1, value / 100) : Math.min(1, value);
}

function normalizeFlowLikeStatus(status) {
  const normalized = String(status ?? "").toLowerCase().trim();
  if (!normalized) {
    return "not_run";
  }
  if (normalized === "pending") {
    return "waiting";
  }
  return normalized;
}

function isCompleteLikeStatus(status) {
  const normalized = normalizeFlowLikeStatus(status);
  return normalized === "complete" || normalized === "error_skip";
}

function isActiveLikeStatus(status) {
  const normalized = normalizeFlowLikeStatus(status);
  return normalized === "running" || normalized === "paused" || normalized === "waiting" || normalized === "not_complete";
}

function derivePhasePercentage({ primitiveStatuses = [], primitiveProgress = [], phaseProgress, effectiveStatus }) {
  const statuses = primitiveStatuses.map(normalizeFlowLikeStatus).filter(Boolean);
  const total = statuses.length;
  const phaseUnit = toUnitProgress(phaseProgress);
  const fallbackProgress = primitiveProgress
    .map((value) => toUnitProgress(value))
    .filter((value) => typeof value === "number");

  if (total === 0) {
    return phaseUnit;
  }

  const completeCount = statuses.filter(isCompleteLikeStatus).length;
  const activeCount = statuses.filter(isActiveLikeStatus).length;
  const statusBased = total > 0 ? completeCount / total : undefined;
  const normalizedEffectiveStatus = normalizeFlowLikeStatus(effectiveStatus);

  if (total > 1) {
    if (completeCount >= total) {
      return 1;
    }
    if (activeCount > 0) {
      return Math.min(statusBased ?? 0, 0.99);
    }
    if (typeof statusBased === "number") {
      return Math.max(0, Math.min(1, statusBased));
    }
  }

  const candidate =
    phaseUnit ??
    (fallbackProgress.length ? Math.max(...fallbackProgress) : undefined) ??
    statusBased;

  if (
    (normalizedEffectiveStatus === "running" || normalizedEffectiveStatus === "waiting") &&
    typeof candidate === "number" &&
    candidate >= 1
  ) {
    return 0.99;
  }
  return candidate;
}

function formatCurrency(value) {
  if (value == null || Number.isNaN(Number(value))) {
    return undefined;
  }
  return usdFormatter.format(Number(value));
}

function safeInstant(timestamp) {
  if (!timestamp) {
    return undefined;
  }
  try {
    return Temporal.Instant.from(timestamp);
  } catch (error) {
    console.warn("Unable to parse timestamp", timestamp, error);
    return undefined;
  }
}

function formatTimestamp(timestamp) {
  const instant = safeInstant(timestamp);
  if (!instant) {
    return undefined;
  }
  return instant
    .toZonedDateTimeISO("UTC")
    .toLocaleString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      month: "short",
      day: "numeric",
    });
}

function formatDuration(started, finished) {
  if (!started) {
    return undefined;
  }
  const startInstant = safeInstant(started);
  if (!startInstant) {
    return undefined;
  }
  const finishedInstant = finished ? safeInstant(finished) : undefined;
  let endInstant = finishedInstant;
  if (endInstant && Temporal.Instant.compare(endInstant, startInstant) < 0) {
    endInstant = undefined;
  }
  if (!endInstant) {
    const now = Temporal.Now.instant();
    endInstant = Temporal.Instant.compare(now, startInstant) >= 0 ? now : startInstant;
  }
  const diff = endInstant.since(startInstant, {
    largestUnit: "hours",
    smallestUnit: "seconds",
    roundingMode: "floor",
  });
  const hours = diff.hours + diff.days * 24;
  const minutes = diff.minutes;
  const seconds = diff.seconds;
  const parts = [];
  if (hours > 0) {
    parts.push(`${hours}h`);
  }
  if (minutes > 0 || hours > 0) {
    parts.push(`${minutes}m`);
  }
  parts.push(`${seconds}s`);
  return parts.join(" ");
}

function ProgressRing({ percent, size = 36, showLabel = true }) {
  const radius = (size - 6) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress =
    typeof percent === "number" ? Math.max(0, Math.min(100, percent)) : null;
  const dashOffset =
    progress == null ? circumference : circumference - (progress / 100) * circumference;

  if (progress == null) {
    return (
      <span className="relative inline-flex h-[36px] w-[36px] items-center justify-center rounded-full bg-slate-100 text-slate-500">
        <svg
          width={size}
          height={size}
          className="animate-spin"
          viewBox={`0 0 ${size} ${size}`}
        >
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="currentColor"
            strokeWidth="3"
            strokeDasharray="8 6"
          />
        </svg>
      </span>
    );
  }

  return (
    <span className="relative inline-flex h-[36px] w-[36px] items-center justify-center rounded-full bg-slate-100 text-[11px] font-semibold text-slate-600">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#e2e8f0"
          strokeWidth="3"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#1f2937"
          strokeWidth="3"
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
          strokeLinecap="round"
        />
      </svg>
      {showLabel && (
        <span className="absolute inset-0 flex items-center justify-center">
          {Math.round(progress)}%
        </span>
      )}
    </span>
  );
}

function resolveEarliestTimestamp(values) {
  if (!Array.isArray(values) || values.length === 0) {
    return undefined;
  }
  let earliest = null;
  let earliestRaw = undefined;
  values.forEach((value) => {
    const instant = safeInstant(value);
    if (!instant) {
      return;
    }
    if (!earliest || Temporal.Instant.compare(instant, earliest) < 0) {
      earliest = instant;
      earliestRaw = value;
    }
  });
  return earliestRaw;
}

function resolveLatestTimestamp(values) {
  if (!Array.isArray(values) || values.length === 0) {
    return undefined;
  }
  let latest = null;
  let latestRaw = undefined;
  values.forEach((value) => {
    const instant = safeInstant(value);
    if (!instant) {
      return;
    }
    if (!latest || Temporal.Instant.compare(instant, latest) > 0) {
      latest = instant;
      latestRaw = value;
    }
  });
  return latestRaw;
}

function getDomainFromUrl(value) {
  if (typeof value !== "string") {
    return null;
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }
  try {
    const normalized = trimmed.startsWith("http") ? trimmed : `https://${trimmed}`;
    const url = new URL(normalized);
    return url.hostname.replace(/^www\./i, "");
  } catch {
    const match = trimmed.match(/([a-z0-9.-]+\.[a-z]{2,})/i);
    return match ? match[1].replace(/^www\./i, "").toLowerCase() : null;
  }
}

function resolveAbsoluteUrl(value) {
  if (typeof value !== "string") {
    return null;
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }
  try {
    const url = new URL(trimmed);
    if (url.protocol === "http:" || url.protocol === "https:") {
      return url.toString();
    }
  } catch {
    // ignore parse errors
  }
  return null;
}

function resolveUrlPreviewImage(value) {
  if (!value || typeof value !== "object") {
    return null;
  }
  const candidates = [
    value.image,
    value.image_url,
    value.imageUrl,
    value.thumbnail,
    value.thumbnail_url,
    value.thumbnailUrl,
    value.preview_image,
    value.previewImage,
    value.og_image,
    value.ogImage,
    value.open_graph_image,
    value.openGraphImage,
    value.preview?.image,
    value.preview?.image_url,
    value.preview?.imageUrl,
    value.preview?.thumbnail,
    value.preview?.thumbnail_url,
    value.preview?.thumbnailUrl,
    value.metadata?.image,
    value.metadata?.image_url,
    value.metadata?.imageUrl,
    value.metadata?.thumbnail,
    value.metadata?.thumbnail_url,
    value.metadata?.thumbnailUrl,
  ];
  for (const candidate of candidates) {
    const absolute = resolveAbsoluteUrl(candidate);
    if (absolute) {
      return absolute;
    }
  }
  return null;
}

function buildPreviewImageSrc(url) {
  const absolute = resolveAbsoluteUrl(url);
  if (!absolute) {
    return null;
  }
  return `/api/preview?url=${encodeURIComponent(absolute)}`;
}

function buildRemoteImageSrc(url) {
  const absolute = resolveAbsoluteUrl(url);
  if (!absolute) {
    return null;
  }
  return `/api/remoteImage?url=${encodeURIComponent(absolute)}`;
}

function toPrimitiveItems(collectionLike) {
  if (!collectionLike) {
    return [];
  }
  if (Array.isArray(collectionLike)) {
    return collectionLike.filter(Boolean);
  }
  if (Array.isArray(collectionLike.allItems)) {
    return collectionLike.allItems.filter(Boolean);
  }
  if (Array.isArray(collectionLike.allUniqueItems)) {
    return collectionLike.allUniqueItems.filter(Boolean);
  }
  return [];
}

function resolveResultPrimitiveUrl(item) {
  if (!item || typeof item !== "object") {
    return null;
  }
  const params = item.referenceParameters ?? {};
  const metadata = item.metadata ?? {};
  const candidates = [
    params.url,
    params.link,
    params.href,
    params.sourceUrl,
    params.source_url,
    params.articleUrl,
    params.article_url,
    params.storyUrl,
    params.story_url,
    params.page_url,
    params.post_url,
    params.external_url,
    metadata.url,
    metadata.link,
    metadata.href,
    metadata.sourceUrl,
    metadata.source_url,
    item.url,
    item.link,
    item.href,
  ];
  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      for (const entry of candidate) {
        const absolute = resolveAbsoluteUrl(entry);
        if (absolute) {
          return absolute;
        }
      }
      continue;
    }
    const absolute = resolveAbsoluteUrl(candidate);
    if (absolute) {
      return absolute;
    }
  }
  return null;
}

function resolveResultPrimitiveImage(item) {
  if (!item || typeof item !== "object") {
    return null;
  }
  const params = item.referenceParameters ?? {};
  const metadata = item.metadata ?? {};
  const candidates = [
    params.image,
    params.image_url,
    params.imageUrl,
    params.main_image,
    params.thumbnail,
    params.thumbnail_url,
    params.thumbnailUrl,
    params.preview_image,
    params.previewImage,
    params.og_image,
    params.ogImage,
    params.logo,
    params.logo_url,
    params.logoUrl,
    params.avatar,
    params.avatar_url,
    params.avatarUrl,
    metadata.image,
    metadata.image_url,
    metadata.imageUrl,
    metadata.thumbnail,
    metadata.thumbnail_url,
    metadata.thumbnailUrl,
    item.avatarImageUrl,
  ];
  for (const candidate of candidates) {
    const absolute = resolveAbsoluteUrl(candidate);
    if (absolute) {
      return absolute;
    }
  }
  return null;
}

function collectUrlEntriesFromValue(value, entries, depth = 0) {
  if (depth > 3 || value == null) {
    return;
  }
  if (typeof value === "string") {
    const direct = resolveAbsoluteUrl(value);
    if (direct) {
      entries.set(direct, {
        url: direct,
        domain: getDomainFromUrl(direct) ?? "link",
        image: null,
        title: null,
      });
    }
    const matches = value.match(/https?:\/\/[^\s)]+/gi) ?? [];
    matches.forEach((match) => {
      const parsed = resolveAbsoluteUrl(match);
      if (parsed) {
        entries.set(parsed, {
          url: parsed,
          domain: getDomainFromUrl(parsed) ?? "link",
          image: null,
          title: null,
        });
      }
    });
    return;
  }
  if (Array.isArray(value)) {
    value.forEach((entry) => collectUrlEntriesFromValue(entry, entries, depth + 1));
    return;
  }
  if (typeof value === "object") {
    const ownUrl = resolveAbsoluteUrl(value.url ?? value.link ?? value.href ?? value.active_url);
    if (ownUrl) {
      const existing = entries.get(ownUrl);
      entries.set(ownUrl, {
        url: ownUrl,
        domain: getDomainFromUrl(ownUrl) ?? "link",
        image: resolveUrlPreviewImage(value) ?? existing?.image ?? null,
        title:
          (typeof value.title === "string" && value.title.trim()) ||
          (typeof value.name === "string" && value.name.trim()) ||
          (typeof value.metadata?.title === "string" && value.metadata.title.trim()) ||
          existing?.title ||
          null,
      });
    }
    Object.values(value).forEach((entry) => collectUrlEntriesFromValue(entry, entries, depth + 1));
  }
}

function getProgressMessages(value, messages, depth = 0) {
  if (depth > 3 || value == null) {
    return;
  }
  if (typeof value === "string") {
    const text = value.trim();
    if (text) {
      messages.add(text);
    }
    return;
  }
  if (Array.isArray(value)) {
    value.forEach((entry) => getProgressMessages(entry, messages, depth + 1));
    return;
  }
  if (typeof value === "object") {
    [value.message, value.text, value.statusMessage].forEach((entry) => {
      if (typeof entry === "string" && entry.trim()) {
        messages.add(entry.trim());
      }
    });
    const statusEntry = typeof value.status === "string" ? value.status.trim() : "";
    const normalizedStatus = statusEntry.toLowerCase();
    if (
      statusEntry &&
      ![
        "running",
        "waiting",
        "pending",
        "queued",
        "complete",
        "completed",
        "success",
        "error",
        "failed",
        "idle",
      ].includes(normalizedStatus)
    ) {
      messages.add(statusEntry);
    }
  }
}

function getProgressQueries(value, queries, depth = 0) {
  if (depth > 3 || value == null) {
    return;
  }
  if (Array.isArray(value)) {
    value.forEach((entry) => getProgressQueries(entry, queries, depth + 1));
    return;
  }
  if (typeof value === "object") {
    [value.active_query, value.query, value.term].forEach((entry) => {
      if (typeof entry === "string" && entry.trim()) {
        queries.add(entry.trim());
      }
    });
  }
}

function getProgressPhases(value, phases, depth = 0) {
  if (depth > 3 || value == null) {
    return;
  }
  if (Array.isArray(value)) {
    value.forEach((entry) => getProgressPhases(entry, phases, depth + 1));
    return;
  }
  if (typeof value === "object") {
    const phase =
      typeof value.phase === "string"
        ? value.phase.trim()
        : typeof value.step === "string"
        ? value.step.trim()
        : "";
    if (phase) {
      phases.add(phase);
    }
  }
}

function getProgressSearchCount(value, depth = 0) {
  if (depth > 3 || value == null) {
    return 0;
  }
  if (typeof value === "object") {
    const candidates = [
      value.search_query_count,
      value.searchQueryCount,
      value.query_count,
      value.queryCount,
    ];
    for (const candidate of candidates) {
      const parsed = Number(candidate);
      if (Number.isFinite(parsed) && parsed >= 0) {
        return parsed;
      }
    }
  }
  return 0;
}

function getProgressUrlExaminedCount(value, depth = 0) {
  if (depth > 3 || value == null) {
    return 0;
  }
  if (typeof value === "object") {
    const candidates = [
      value.url_examined_count,
      value.urlExaminedCount,
      value.urls_examined_count,
      value.urlsExaminedCount,
      value.urls_examined,
      value.totalScanned,
      value.scanned,
    ];
    for (const candidate of candidates) {
      const parsed = Number(candidate);
      if (Number.isFinite(parsed) && parsed >= 0) {
        return parsed;
      }
    }
  }
  return 0;
}

function normalizeProgressMessageShape(message) {
  if (typeof message !== "string") {
    return "";
  }
  return message
    .toLowerCase()
    .replace(/\d+(?:[.,]\d+)?/g, "#")
    .replace(/\s+/g, " ")
    .trim();
}

function shouldMergeHistoryProgressMessage(previousMessage, nextMessage) {
  if (typeof previousMessage !== "string" || typeof nextMessage !== "string") {
    return false;
  }
  if (!/\d/.test(previousMessage) || !/\d/.test(nextMessage)) {
    return false;
  }
  const previousShape = normalizeProgressMessageShape(previousMessage);
  const nextShape = normalizeProgressMessageShape(nextMessage);
  if (!previousShape || previousShape !== nextShape) {
    return false;
  }
  return /(found|scanned|max|processed|items|results|collected|count|query|queries|%|fetching|wikidata|wikipedia|entity|candidate|profile|enrichment)/i.test(
    `${previousMessage} ${nextMessage}`,
  );
}

function isGenericPercentMessage(message) {
  if (typeof message !== "string") {
    return false;
  }
  const trimmed = message.trim().toLowerCase();
  return /^\d+(?:\.\d+)?%\s*(complete)?$/.test(trimmed);
}

function isGenericStatusOnlyMessage(message) {
  if (typeof message !== "string") {
    return false;
  }
  const normalized = message.trim().toLowerCase();
  return [
    "running",
    "paused",
    "waiting",
    "pending",
    "queued",
    "complete",
    "completed",
    "success",
    "error",
    "failed",
    "idle",
  ].includes(normalized);
}

function buildFallbackLiveMessage(phaseName, phaseStatus, percent) {
  const name = typeof phaseName === "string" && phaseName.trim() ? phaseName.trim() : "workflow step";
  const status = normalizeFlowLikeStatus(phaseStatus);
  if (status === "paused") {
    return `Waiting for confirmation to continue ${name.toLowerCase()}`;
  }
  if (status === "waiting") {
    return `Waiting to continue ${name.toLowerCase()}`;
  }
  if (status === "running") {
    return `Running ${name.toLowerCase()}`;
  }
  if (status === "complete") {
    return `${name} complete`;
  }
  return `Working on ${name.toLowerCase()}`;
}

function parseSearchQueryMessage(message) {
  if (typeof message !== "string") {
    return null;
  }
  const trimmed = message.trim();
  const match = trimmed.match(/^(searching web:|running search query:)\s*(.+)$/i);
  if (!match?.[2]) {
    return null;
  }
  return {
    query: match[2].trim(),
    sourceMessage: trimmed,
  };
}

function extractPercentFromMessage(message) {
  if (typeof message !== "string") {
    return undefined;
  }
  const match = message.match(/(\d+(?:\.\d+)?)\s*%/);
  if (!match?.[1]) {
    return undefined;
  }
  const parsed = Number(match[1]);
  if (!Number.isFinite(parsed)) {
    return undefined;
  }
  return Math.max(0, Math.min(100, parsed));
}

function extractPercentFromProgressValue(value, depth = 0) {
  if (depth > 3 || value == null) {
    return undefined;
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return safePercent(value);
  }
  if (typeof value === "string") {
    return extractPercentFromMessage(value);
  }
  if (Array.isArray(value)) {
    const values = value
      .map((entry) => extractPercentFromProgressValue(entry, depth + 1))
      .filter((entry) => typeof entry === "number");
    if (!values.length) {
      return undefined;
    }
    return Math.max(...values);
  }
  if (typeof value === "object") {
    const explicitPercent = safePercent(value.percentage ?? value.percent ?? value.progress);
    if (typeof explicitPercent === "number") {
      return explicitPercent;
    }
    const current = Number(value.current ?? value.completed ?? value.processed);
    const total = Number(value.total ?? value.max);
    if (Number.isFinite(current) && Number.isFinite(total) && total > 0) {
      return safePercent((current / total) * 100);
    }
    const nested = Object.values(value)
      .map((entry) => extractPercentFromProgressValue(entry, depth + 1))
      .filter((entry) => typeof entry === "number");
    if (!nested.length) {
      return undefined;
    }
    return Math.max(...nested);
  }
  return undefined;
}

function isDuplicateProgressLine(message, phaseName) {
  if (typeof message !== "string" || typeof phaseName !== "string") {
    return false;
  }
  const normalizedMessage = message.trim().toLowerCase();
  const normalizedPhase = phaseName.trim().toLowerCase();
  if (!normalizedMessage || !normalizedPhase) {
    return false;
  }
  const withoutPercentSuffix = normalizedMessage.replace(/\s*\(\d+(?:\.\d+)?%\)\s*$/, "").trim();
  return (
    normalizedMessage === normalizedPhase ||
    withoutPercentSuffix === normalizedPhase ||
    normalizedMessage === `${normalizedPhase} in progress` ||
    withoutPercentSuffix === `${normalizedPhase} in progress` ||
    normalizedMessage === `running ${normalizedPhase}` ||
    withoutPercentSuffix === `running ${normalizedPhase}` ||
    normalizedMessage === `${normalizedPhase} complete` ||
    withoutPercentSuffix === `${normalizedPhase} complete`
  );
}

function isDuplicateAgainstHeadline(message, headline, activeQuery) {
  if (typeof message !== "string") {
    return false;
  }
  const normalized = message.trim().toLowerCase();
  if (!normalized) {
    return false;
  }
  const normalizedNoPercent = normalized.replace(/\s*\(\d+(?:\.\d+)?%\)\s*$/, "").trim();
  const normalizedHeadline = typeof headline === "string" ? headline.trim().toLowerCase() : "";
  if (
    normalizedHeadline &&
    (normalized === normalizedHeadline || normalizedNoPercent === normalizedHeadline)
  ) {
    return true;
  }
  const parsedSearch = parseSearchQueryMessage(message);
  if (parsedSearch?.query && typeof activeQuery === "string" && activeQuery.trim()) {
    const a = parsedSearch.query.trim().toLowerCase();
    const b = activeQuery.trim().toLowerCase();
    if (a === b) {
      return true;
    }
  }
  return false;
}

function isZeroSearchMetricsMessage(message) {
  if (typeof message !== "string") {
    return false;
  }
  const trimmed = message.trim();
  if (!trimmed) {
    return false;
  }
  return /^found\s+0\s+items\s+\(scanned\s+0(?:\s*\/\s*max\s+\d+)?\)$/i.test(trimmed);
}

function isLifecycleStatusLogMessage(message) {
  return isGenericStatusOnlyMessage(message);
}

function normalizeActionLogMessage(message) {
  if (typeof message !== "string") {
    return "";
  }
  return message
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

function coalesceActionLogHistory(entries = []) {
  if (!Array.isArray(entries) || entries.length < 2) {
    return Array.isArray(entries) ? entries : [];
  }
  const output = [];
  entries.forEach((entry) => {
    const message = entry?.message;
    const normalized = normalizeActionLogMessage(message);
    if (!normalized) {
      output.push(entry);
      return;
    }
    const prev = output[output.length - 1];
    const prevNormalized = normalizeActionLogMessage(prev?.message);
    if (prev && prevNormalized === normalized) {
      output[output.length - 1] = {
        ...entry,
        id: prev.id,
      };
      return;
    }
    output.push(entry);
  });
  return output;
}

function toPhaseLabel(phase) {
  if (typeof phase !== "string") {
    return "";
  }
  return phase
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function resolvePrimitiveSearchQueryCount(primitive) {
  let best = 0;
  if (primitive?.processing && typeof primitive.processing === "object") {
    Object.values(primitive.processing).forEach((entry) => {
      if (!entry || typeof entry !== "object") {
        return;
      }
      best = Math.max(best, getProgressSearchCount(entry, 1));
      if (entry.progress && typeof entry.progress === "object") {
        best = Math.max(best, getProgressSearchCount(entry.progress, 2));
      }
    });
  }
  const statsCount = Number(
    primitive?.progressStats?.search_query_count ??
      primitive?.progressStats?.searchQueryCount ??
      primitive?.progressStats?.query_count ??
      primitive?.progressStats?.queryCount,
  );
  if (Number.isFinite(statsCount) && statsCount > 0) {
    best = Math.max(best, statsCount);
  }
  const scannedFallback = Number(
    primitive?.progressStats?.totalScanned ??
      primitive?.processing?.query?.totalScanned ??
      primitive?.processing?.query?.scanned,
  );
  if (
    best === 0 &&
    primitive?.type === "search" &&
    Number.isFinite(scannedFallback) &&
    scannedFallback > 0
  ) {
    best = 1;
  }
  return best;
}

function resolvePrimitiveUrlExaminedCount(primitive) {
  let best = 0;
  if (primitive?.processing && typeof primitive.processing === "object") {
    Object.values(primitive.processing).forEach((entry) => {
      if (!entry || typeof entry !== "object") {
        return;
      }
      best = Math.max(best, getProgressUrlExaminedCount(entry, 1));
      if (entry.progress && typeof entry.progress === "object") {
        best = Math.max(best, getProgressUrlExaminedCount(entry.progress, 2));
      }
    });
  }
  const scannedCandidates = [
    primitive?.progressStats?.totalScanned,
    primitive?.progressStats?.scanned,
    primitive?.processing?.query?.totalScanned,
    primitive?.processing?.query?.scanned,
  ];
  scannedCandidates.forEach((candidate) => {
    const parsed = Number(candidate);
    if (Number.isFinite(parsed) && parsed >= 0) {
      best = Math.max(best, parsed);
    }
  });
  return best;
}

function appendPrimitiveProgressCandidates(primitive, candidates, seen, depth = 0) {
  if (!primitive || depth > 2) {
    return;
  }
  const primitiveId = primitive.id ?? primitive._id ?? null;
  if (primitiveId && seen.has(primitiveId)) {
    return;
  }
  if (primitiveId) {
    seen.add(primitiveId);
  }

  if (primitive.progress !== undefined) {
    candidates.push(primitive.progress);
  }
  if (primitive.progressMessage !== undefined) {
    candidates.push(primitive.progressMessage);
  }
  if (primitive.percentageProgress !== undefined) {
    candidates.push({ percent: primitive.percentageProgress });
  }

  const processing = primitive.processing;
  if (processing && typeof processing === "object") {
    Object.values(processing).forEach((value) => {
      if (value && typeof value === "object") {
        if (value.progress !== undefined) {
          candidates.push(value.progress);
        } else {
          candidates.push(value);
        }
      }
    });
  }

  const children = primitive.primitives?.origin?.allItems ?? [];
  if (primitive.type === "query" && children.length > 0) {
    const candidateChildren = children.filter(
      (child) => child && PROGRESS_CANDIDATE_TYPES.has(child.type),
    );
    if (candidateChildren.length > 0) {
      const completeCount = candidateChildren.filter((child) => {
        const status = String(
          child?.processing?.flow?.status ??
            child?.processing?.run_step?.status ??
            child?.processing?.query?.status ??
            "",
        )
          .trim()
          .toLowerCase();
        return status === "complete" || status === "completed" || status === "success";
      }).length;
      candidates.push({
        current: completeCount,
        total: candidateChildren.length,
      });
      candidateChildren.forEach((child) => {
        if (child?.percentageProgress !== undefined) {
          candidates.push({ percent: child.percentageProgress });
        }
      });
    }
  }
  children.forEach((child) => {
    if (!child || !PROGRESS_CANDIDATE_TYPES.has(child.type)) {
      return;
    }
    appendPrimitiveProgressCandidates(child, candidates, seen, depth + 1);
  });
}

function resolveCompanyLogoUrl(item) {
  if (!item) {
    return null;
  }
  const domainCandidates = [
    item.metadata?.domain,
    item.referenceParameters?.domain,
    getDomainFromUrl(item.referenceParameters?.url),
    getDomainFromUrl(item.referenceParameters?.website),
    getDomainFromUrl(item.metadata?.url),
  ].filter(Boolean);
  if (domainCandidates.length > 0) {
    return `/api/companyLogo?domain=${encodeURIComponent(domainCandidates[0])}`;
  }
  const label = item.metadata?.title || item.metadata?.name || item.title;
  return label ? `/api/companyLogo?name=${encodeURIComponent(label)}` : null;
}

function toFiniteRating(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return undefined;
  }
  return parsed;
}

function resolveItemRating(item) {
  if (!item || typeof item !== "object") {
    return null;
  }
  const params = item.referenceParameters ?? {};
  const metadata = item.metadata ?? {};
  const ratingCandidates = [
    params.product_rating,
    params.review_rating,
    params.rating,
    params.stars,
    params.score,
    metadata.product_rating,
    metadata.review_rating,
    metadata.rating,
    item.product_rating,
    item.review_rating,
    item.rating,
  ];
  let rawRating;
  for (const candidate of ratingCandidates) {
    const parsed = toFiniteRating(candidate);
    if (parsed !== undefined) {
      rawRating = parsed;
      break;
    }
  }
  if (rawRating === undefined) {
    return null;
  }

  const maxCandidates = [
    params.product_rating_max,
    params.review_rating_max,
    params.rating_max,
    params.max_rating,
    metadata.product_rating_max,
    metadata.review_rating_max,
    metadata.rating_max,
    item.product_rating_max,
    item.review_rating_max,
    item.rating_max,
  ];
  let rawMax;
  for (const candidate of maxCandidates) {
    const parsed = toFiniteRating(candidate);
    if (parsed !== undefined && parsed > 0) {
      rawMax = parsed;
      break;
    }
  }
  const max = rawMax ?? 5;
  if (!(max > 0)) {
    return null;
  }
  const normalized = Math.max(0, Math.min(5, (rawRating / max) * 5));
  return {
    value: normalized,
    raw: rawRating,
    max,
  };
}

function resolvePreferredEvidenceImage(item) {
  if (!item || typeof item !== "object") {
    return { directSrc: null, urlPreviewSrc: null, fallbackSrc: item?.avatarImageUrl ?? null };
  }
  const params = item.referenceParameters ?? {};
  const metadata = item.metadata ?? {};
  const directImageCandidates = [
    params.cover_image_url,
    params.coverImageUrl,
    params.cover_url,
    params.coverUrl,
    params.image_url,
    params.imageUrl,
    params.main_image,
    params.thumbnail_url,
    params.thumbnailUrl,
    metadata.cover_image_url,
    metadata.coverImageUrl,
    metadata.image_url,
    metadata.imageUrl,
  ];
  for (const candidate of directImageCandidates) {
    const absolute = resolveAbsoluteUrl(candidate);
    if (absolute) {
      return {
        directSrc: buildRemoteImageSrc(absolute),
        urlPreviewSrc: null,
        fallbackSrc: item.avatarImageUrl ?? null,
      };
    }
  }

  const productUrlCandidates = [
    params.product_url,
    params.productUrl,
    params.url,
    params.link,
    metadata.product_url,
    metadata.productUrl,
    metadata.url,
    metadata.link,
  ];
  for (const candidate of productUrlCandidates) {
    const absolute = resolveAbsoluteUrl(candidate);
    if (absolute) {
      return {
        directSrc: null,
        urlPreviewSrc: buildPreviewImageSrc(absolute),
        fallbackSrc: item.avatarImageUrl ?? null,
      };
    }
  }

  return { directSrc: null, urlPreviewSrc: null, fallbackSrc: item.avatarImageUrl ?? null };
}

function EvidenceAvatar({ item, className }) {
  const rating = resolveItemRating(item);
  const preferredImage = resolvePreferredEvidenceImage(item);
  if (!preferredImage?.directSrc && rating) {
    const fillPct = Math.max(0, Math.min(100, (rating.value / 5) * 100));
    return (
      <div
        className={clsx(
          "relative inline-flex items-center justify-center rounded-full border border-slate-300 bg-white text-slate-500",
          className,
        )}
        title={`${rating.raw.toFixed(rating.raw % 1 === 0 ? 0 : 1)}/${rating.max} rating`}
      >
        <span className="relative block text-[18px] leading-none">
          <span className="text-slate-300">★</span>
          <span
            className="absolute left-0 top-0 overflow-hidden whitespace-nowrap text-amber-500"
            style={{ width: `${fillPct}%` }}
          >
            ★
          </span>
        </span>
      </div>
    );
  }
  return (
    <Avatar
      isBordered
      src={preferredImage?.directSrc ?? preferredImage?.urlPreviewSrc ?? preferredImage?.fallbackSrc}
      name={item?.title || item?.metadata?.title || "item"}
      showFallback
      className={className}
      imgProps={{ referrerPolicy: "no-referrer" }}
    />
  );
}


function buildMetricsForPrimitive(primitive) {
  if (!primitive) {
    return [];
  }

  const baseLabel =
    primitive.configParent?.title ??
    primitive.referenceParameters?.labelForMap ??
    primitive.title ??
    `#${primitive.plainId}`;

  const metrics = [];
  const stats = primitive.progressStats;
  const runnerProgress = primitive.processing?.run_runner?.progress;

  if (primitive.type === "search" && stats) {
    const scannedValue =
      typeof stats.totalScanned === "number" ? stats.totalScanned : undefined;
    const keptValue =
      typeof stats.totalCount === "number" ? stats.totalCount : undefined;
    const maxValue =
      typeof stats.max === "number" ? stats.max : undefined;
    if (scannedValue != null || keptValue != null) {
      metrics.push({
        key: `${baseLabel}::search`,
        type: "searchStats",
        label: baseLabel,
        scannedValue,
        keptValue,
        maxValue,
      });
    }
    return metrics;
  }

  if (
    primitive.type !== "search" &&
    stats &&
    (typeof stats.totalScanned === "number" ||
      typeof stats.totalCount === "number")
  ) {
    const maxValue =
      typeof stats.max === "number"
        ? stats.max
        : typeof stats.totalCount === "number"
        ? stats.totalCount
        : undefined;
    metrics.push({
      key: `${baseLabel}::stats`,
      type: "simple",
      label: baseLabel,
      currentValue:
        typeof stats.totalScanned === "number" ? stats.totalScanned : undefined,
      maxValue,
    });
  }

  if (
    primitive.type === "actionrunner" &&
    runnerProgress &&
    (typeof runnerProgress === "object" || typeof runnerProgress === "number")
  ) {
    const currentValue =
      typeof runnerProgress?.current === "number"
        ? runnerProgress.current
        : typeof runnerProgress?.completed === "number"
        ? runnerProgress.completed
        : typeof runnerProgress?.processed === "number"
        ? runnerProgress.processed
        : undefined;
    const maxValue =
      typeof runnerProgress?.total === "number"
        ? runnerProgress.total
        : typeof runnerProgress?.max === "number"
        ? runnerProgress.max
        : undefined;
    if (currentValue != null || maxValue != null) {
      metrics.push({
        key: `${baseLabel}::runner`,
        type: "simple",
        label: baseLabel,
        currentValue,
        maxValue,
      });
    }
  }

  if (metrics.length === 0) {
    const text =
      (typeof runnerProgress?.message === "string" ? runnerProgress.message : null) ??
      primitive.progress ??
      primitive.progressMessage;
    if (text) {
      metrics.push({
        key: `${primitive.id}::text`,
        type: "text",
        label: baseLabel,
        text,
      });
    }
  }

  return metrics;
}

function mergePhaseMetrics(items) {
  const byKey = new Map();
  for (const primitive of items) {
    for (const metric of buildMetricsForPrimitive(primitive)) {
      const key = metric.key ?? metric.label;
      const existing = byKey.get(key);
      if (!existing) {
        byKey.set(key, { ...metric });
        continue;
      }

      switch (metric.type) {
        case "searchStats": {
          existing.scannedValue =
            (existing.scannedValue ?? 0) + (metric.scannedValue ?? 0);
          existing.keptValue =
            (existing.keptValue ?? 0) + (metric.keptValue ?? 0);
          break;
        }
        case "text": {
          existing.text = existing.text
            ? `${existing.text}\n${metric.text}`
            : metric.text;
          break;
        }
        default: {
          if (typeof metric.currentValue === "number") {
            existing.currentValue =
              (existing.currentValue ?? 0) + metric.currentValue;
          }
          if (typeof metric.maxValue === "number") {
            existing.maxValue =
              (existing.maxValue ?? 0) + metric.maxValue;
          }
        }
      }
    }
  }
  return Array.from(byKey.values());
}

function usePhases(primitive, refreshTick) {
  const [phaseData, setPhaseData] = useState({
    phases: [],
    edges: [],
    statusMap: null,
  });

  useEffect(() => {
    if (!primitive?.instanceStatus) {
      return;
    }
    let cancelled = false;
    async function load() {
      try {
        const status = await primitive.instanceStatus;
        if (cancelled) {
          return;
        }
        const { nodes, edges } = PrimitiveConfig.flowInstanceStatusToMap(
          status,
          { showHidden: false, showSkipped: false, groupByLabels: true },
        );
        const depth = PrimitiveConfig.convertEdgesToDepth(edges);
        const phases = nodes
          .map((node) => ({
            ...node,
            depth: depth.get(node.id) ?? 0,
          }))
          .sort((a, b) => (a.depth ?? 0) - (b.depth ?? 0));
        setPhaseData({ phases, edges, statusMap: status });
      } catch (error) {
        console.error("Failed to load instance status", error);
        setPhaseData({ phases: [], edges: [], statusMap: null });
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [primitive?.id, refreshTick]);

  return phaseData;
}

export default function WorkflowInstanceStatus({
  primitive,
  className,
  showCost = false,
  showControls = true,
  variant = "inline",
}) {
  const [refreshTick, forceRefresh] = useReducer((value) => value + 1, 0);
  const [, forceClockTick] = useReducer((value) => value + 1, 0);
  const { phases, statusMap } = usePhases(primitive, refreshTick);
  const [costSnapshot, setCostSnapshot] = useState(null);
  const [costError, setCostError] = useState(null);
  const [highlightedRunningIndex, setHighlightedRunningIndex] = useState(0);
  const [showAllSteps, setShowAllSteps] = useState(false);
  const [selectedLiveStepId, setSelectedLiveStepId] = useState(undefined);
  const [heroProgressHistory, setHeroProgressHistory] = useState([]);
  const [heroStageHistory, setHeroStageHistory] = useState([]);
  const progressHistoryRef = useRef(new Map());
  const stageHistoryRef = useRef(new Map());
  const heroProgressLogRef = useRef(null);
  const heroUrlGalleryRef = useRef(null);
  const heroUrlGalleryViewportRef = useRef(null);

  const activeWorkspaceId = MainStore().activeWorkspaceId;
  const workspaces = MainStore().workspaces?.() ?? [];
  const activeWorkspace = useMemo(() => {
    if (!activeWorkspaceId) {
      return undefined;
    }
    return workspaces.find((workspace) => {
      const matchId = workspace.id ?? workspace._id;
      return `${matchId}` === `${activeWorkspaceId}`;
    });
  }, [workspaces, activeWorkspaceId]);
  const { accentBorderClassName } = useWorkspaceBranding(activeWorkspace);

  const phaseWatchIds = useMemo(() => {
    if (!statusMap) {
      return [];
    }
    return Object.values(statusMap)
      .map((entry) => entry.primitive?.id)
      .filter(Boolean);
  }, [statusMap]);

  const watchIds = useMemo(
    () => [primitive?.id, ...phaseWatchIds].filter(Boolean),
    [primitive?.id, phaseWatchIds],
  );

  useDataEvent("set_field set_parameter", watchIds, () => forceRefresh());
  useDataEvent(
    "relationship_update add_primitives remove_primitives new_primitives",
    primitive?.id ? [primitive.id] : [],
    () => forceRefresh(),
  );

  const mainstore = MainStore();
  const isExternalUser = Boolean(mainstore.activeUser?.info?.external);

  useEffect(() => {
    if (!primitive?.id || isExternalUser) {
      setCostSnapshot(null);
      setCostError(null);
      return;
    }
    let cancelled = false;
    async function loadCost() {
      try {
        const response = await fetch(`/api/flow_instances/${primitive.id}/cost`, {
          credentials: "include",
        });
        if (!response.ok) {
          throw new Error(`Request failed with ${response.status}`);
        }
        const json = await response.json();
        if (!cancelled) {
          setCostSnapshot(json);
          setCostError(null);
        }
      } catch (error) {
        if (!cancelled) {
          setCostError(error);
        }
      }
    }
    loadCost();
    const interval = setInterval(loadCost, 5000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [primitive?.id, isExternalUser]);

  const phaseDetails = useMemo(() => {
    return phases.map((phase) => {
      const primitives =
        phase.itemIds?.map((id) => mainstore.primitive(id)).filter(Boolean) ??
        [];
      const statusEntries =
        phase.itemIds?.map((id) => statusMap?.[id]).filter(Boolean) ?? [];
      const subflowSummaries = primitives
        .filter((primitive) => primitive?.type === "flowinstance")
        .map((primitive) => {
          const progress = primitive.processing?.flow?.progress;
          return {
            id: primitive.id,
            name:
              primitive.referenceParameters?.labelForMap ??
              primitive.origin?.title ??
              primitive.title ??
              `Subflow ${primitive.plainId}`,
            status: primitive.processing?.flow?.status ?? "not_started",
            percent: safePercent(progress?.percent),
            started: primitive.processing?.flow?.started,
          };
        });
      const subflowCounts = subflowSummaries.reduce(
        (acc, subflow) => {
          acc.total += 1;
          const status = (subflow.status ?? "").toLowerCase();
          let bucket = "pending";
          if (status === "running" || status === "rerun") {
            bucket = "running";
          } else if (status === "waiting" || status === "queued" || status === "paused") {
            bucket = "waiting";
          } else if (status === "complete") {
            bucket = "complete";
          } else if (status === "error" || status === "failed" || status === "error_skip") {
            bucket = "error";
          }
          acc[bucket] = (acc[bucket] ?? 0) + 1;
          return acc;
        },
        { total: 0 },
      );
      const activeSubflow =
        subflowSummaries.find((sub) => sub.status === "running") ??
        subflowSummaries.find((sub) => sub.status === "waiting") ??
        subflowSummaries[0];
      const rawStatus = typeof phase.status === "function" ? phase.status() : "not_run";
      const scaffoldingEntry = statusEntries.find(
        (entry) => entry?.needReason === "scaffold_flow" && entry?.need,
      );
      const pausedEntry = statusEntries.find(
        (entry) => entry?.needReason === "awaiting_confirmation" && entry?.need,
      );
      const effectiveStatus =
        pausedEntry
          ? "paused"
          : scaffoldingEntry && subflowSummaries.length === 0
          ? "scaffolding"
          : rawStatus;
      const rawPhasePercentage = phase.progress?.();
      const primitiveStatuses = primitives.map((item) => {
        if (!item) {
          return "not_run";
        }
        if (item.processing?.run_step?.status === "pending") {
          return "waiting";
        }
        return item.processing?.flow?.status ?? "not_run";
      });
      const primitiveProgress = primitives.map((item) => item?.percentageProgress);
      const phasePercentage = derivePhasePercentage({
        primitiveStatuses,
        primitiveProgress,
        phaseProgress: rawPhasePercentage,
        effectiveStatus,
      });

      let message = phase.progressMessage?.();
      if (pausedEntry) {
        message = "Waiting for confirmation";
      }
      if (scaffoldingEntry && subflowSummaries.length === 0) {
        const label =
          scaffoldingEntry.primitive?.title ??
          scaffoldingEntry.primitive?.plainId ??
          phase.name;
        message = `Preparing ${label ?? "subflow"}…`;
      }
      if (!message && activeSubflow) {
        let descriptor = activeSubflow.status;
        if (activeSubflow.status === "running" && typeof activeSubflow.percent === "number") {
            descriptor = `${Math.round(activeSubflow.percent)}%`;
        } else if (activeSubflow.status === "complete") {
          descriptor = "Complete";
        } else if (activeSubflow.status === "error") {
          descriptor = "Error";
        } else if (activeSubflow.status === "waiting") {
          descriptor = "Waiting";
        } else if (!descriptor) {
          descriptor = "Pending";
        }
        message = `${activeSubflow.name}: ${descriptor}`;
      }
      if (statusEntries.length > 0) {
        const activeSearch = statusEntries.find((entry) => {
          const primitive = entry?.primitive;
          if (!primitive || primitive.type !== "search") {
            return false;
          }
          const queryStatus =
            primitive.processing?.query?.status ??
            primitive.processing?.flow?.status;
          return (
            queryStatus === "running" ||
            queryStatus === "pending" ||
            primitive.processing?.query?.collecting
          );
        });
        const searchTerm =
          activeSearch?.primitive?.processing?.query?.progress?.term ??
          activeSearch?.primitive?.processing?.query?.term ??
          activeSearch?.primitive?.processing?.query?.keyword;
        if (searchTerm) {
          const sanitizedTerm =
            typeof searchTerm === "string" ? searchTerm.trim() : String(searchTerm);
          const suffix = message ? ` · ${message}` : "";
          message = `Searching for "${sanitizedTerm}"${suffix}`;
        }
      }

      const phaseStartedRaw = resolveEarliestTimestamp(
        primitives
          .map((primitive) => primitive?.processing?.flow?.started)
          .filter(Boolean),
      );
      const phaseCompletedRaw = resolveLatestTimestamp(
        primitives
          .map((primitive) => primitive?.processing?.flow?.completed)
          .filter(Boolean),
      );

      return {
        id: phase.id,
        name: phase.name,
        status: effectiveStatus,
        rawStatus,
        icon: phase.icon,
        percentage: phasePercentage,
        message,
        startedAt: formatTimestamp(phaseStartedRaw),
        duration: formatDuration(phaseStartedRaw, phaseCompletedRaw),
        primitives,
        metrics: mergePhaseMetrics(primitives),
        statusEntries,
        pausedEntry,
        scaffolding: Boolean(scaffoldingEntry),
        subflows: subflowSummaries,
        subflowCounts,
        activeSubflow,
      };
    });
  }, [phases, mainstore, refreshTick, statusMap]);

  const overallProgress = primitive?.processing?.flow?.progress;
  let percentComplete = safePercent(overallProgress?.percent);
  if (percentComplete == null) {
    if (phaseDetails.length) {
      const completedRatio =
        phaseDetails.filter((phase) => phase.status === "complete").length /
        phaseDetails.length;
      percentComplete = safePercent(completedRatio);
    }
  }
  if (percentComplete == null) {
    percentComplete = 0;
  } else {
    percentComplete = Math.round(percentComplete);
  }

  const trackedCount =
    overallProgress?.total && overallProgress.total > 0
      ? overallProgress.total
      : phaseDetails.length;
  const completedCount =
    overallProgress?.completed ??
    phaseDetails.filter((phase) => phase.status === "complete").length;
  const runningCount =
    overallProgress?.running ??
    phaseDetails.filter(
      (phase) =>
        phase.status === "running" ||
        (phase.subflows?.some((sub) => sub.status === "running") ?? false),
    ).length;
  const failedCount =
    overallProgress?.failed ??
    phaseDetails.filter((phase) => phase.status === "error").length;

  const startedRaw = primitive?.processing?.flow?.started;
  const completedRawCandidate = primitive?.processing?.flow?.completed;
  const startedInstant = safeInstant(startedRaw);
  const completedInstantCandidate = completedRawCandidate
    ? safeInstant(completedRawCandidate)
    : undefined;
  const completedRaw =
    startedInstant &&
    completedInstantCandidate &&
    Temporal.Instant.compare(completedInstantCandidate, startedInstant) < 0
      ? undefined
      : completedRawCandidate;
  const startedAt = formatTimestamp(startedRaw);
  const completedAt = formatTimestamp(completedRaw);
  const elapsed = formatDuration(startedRaw, completedRaw);
  const isFlowComplete = Boolean(completedRaw) || primitive?.processing?.flow?.status === "complete";
  useEffect(() => {
    if (!startedRaw || completedRaw) {
      return undefined;
    }
    const interval = setInterval(() => {
      forceClockTick();
    }, 1000);
    return () => clearInterval(interval);
  }, [startedRaw, completedRaw]);
  const eta =
    overallProgress?.eta ??
    (primitive?.processing?.flow?.estimatedCompletion
      ? formatTimestamp(primitive.processing.flow.estimatedCompletion)
      : undefined);

  const runningPhases = phaseDetails.filter(
    (phase) =>
      phase.status === "running" ||
      (phase.subflows?.some((sub) => sub.status === "running") ?? false),
  );
  const FORCE_SWARM_TEST = false;
  const phaseOrderIndex = useMemo(() => {
    const index = new Map();
    phaseDetails.forEach((phase, idx) => {
      index.set(`${phase?.id}`, idx);
    });
    return index;
  }, [phaseDetails]);
  const heroRunningPhases = useMemo(() => {
    if (FORCE_SWARM_TEST) {
      const desiredTypes = ["search", "query", "categorizer", "summary"];
      const candidates = [];
      phaseDetails.forEach((phase) => {
        (phase.primitives ?? []).forEach((primitive) => {
          if (primitive && desiredTypes.includes(primitive.type)) {
            candidates.push({ primitive, phase });
          }
        });
      });
      const picked = [];
      desiredTypes.forEach((type) => {
        const match = candidates.find((entry) => entry.primitive.type === type);
        if (match && !picked.find((entry) => entry.primitive.id === match.primitive.id)) {
          picked.push(match);
        }
      });
      if (picked.length === 0) {
        const fallback = phaseDetails.slice(0, 4);
        return fallback.length ? fallback : runningPhases;
      }
      const padded = [...picked];
      let idx = 0;
      while (padded.length < 8) {
        padded.push(picked[idx % picked.length]);
        idx += 1;
      }
      return padded.slice(0, 8).map((entry, idx) => {
        const primitive = entry.primitive;
        const phase = entry.phase;
        return {
          ...phase,
          id: `${phase.id ?? primitive.id ?? phase.name ?? "phase"}_test_${idx}`,
          name: primitive?.title ?? phase.name ?? `Step ${idx + 1}`,
          status: "running",
          icon: primitive?.metadata?.icon ?? phase.icon,
          primitives: [primitive],
        };
      });
    }
    return [...runningPhases].sort((a, b) => {
      const aIdx = phaseOrderIndex.get(`${a?.id}`) ?? Number.MAX_SAFE_INTEGER;
      const bIdx = phaseOrderIndex.get(`${b?.id}`) ?? Number.MAX_SAFE_INTEGER;
      if (aIdx !== bIdx) {
        return aIdx - bIdx;
      }
      return `${a?.id ?? ""}`.localeCompare(`${b?.id ?? ""}`);
    });
  }, [runningPhases, phaseDetails, phaseOrderIndex]);
  const runningPhase =
    runningPhases[0] ??
    phaseDetails.find((phase) => phase.scaffolding) ??
    phaseDetails.find((phase) =>
      ["paused", "waiting", "not_complete", "not_executed"].includes(phase.status),
    );

  const completedPhases = phaseDetails.filter(
    (phase) => phase.status === "complete",
  );

  const upcomingPhases = phaseDetails.filter((phase) =>
    ["not_run", "waiting", "not_complete", "not_executed", "scaffolding"].includes(
      phase.status,
    ),
  );

  const progressMessage =
    runningPhase?.message ??
    primitive?.processing?.flow?.message ??
    primitive?.processing?.flow?.statusMessage;

  const rootTotals = costSnapshot?.totals?.rootRun;
  const rootBreakdown =
    costSnapshot?.roots?.find(
      (entry) => entry.flowRootInstanceId === primitive?.id,
    ) ?? costSnapshot?.roots?.[0];
  const inclusiveTotals = costSnapshot?.totals?.inclusive;
  const resolvedRunTotals =
    rootTotals?.records ? rootTotals : rootBreakdown ?? inclusiveTotals;
  const totalRunCost = resolvedRunTotals?.cost;

  const resources = costSnapshot?.resources ?? [];
  const accumulateResources = (entries = []) =>
    entries.reduce(
      (acc, entry) => {
        acc.cost += Number(entry?.cost ?? 0);
        const operations = Number(entry?.totalOperations ?? entry?.totalTokens ?? 0);
        acc.totalTokens += operations;
        acc.promptTokens += Number(entry?.promptTokens ?? 0);
        acc.completionTokens += Number(entry?.completionTokens ?? 0);
        acc.records += Number(entry?.records ?? 0);
        return acc;
      },
      { cost: 0, totalTokens: 0, promptTokens: 0, completionTokens: 0, records: 0 },
    );

  const aiResources = resources.filter((entry) => {
    const id = entry?.resource ?? "";
    return (
      id.startsWith("openai-") ||
      id.startsWith("groq") ||
      id.startsWith("openrouter")
    );
  });
  const externalResources = resources.filter((entry) => !aiResources.includes(entry));

  const aiTotals = accumulateResources(aiResources);
  const externalTotals = accumulateResources(externalResources);
  const aiCost = aiTotals.cost;
  const externalCost = externalTotals.cost;

  const aiTokens =
    aiTotals.totalTokens > 0 ? aiTotals.totalTokens : resolvedRunTotals?.totalTokens ?? 0;
  const aiPromptTokens =
    aiTotals.promptTokens > 0
      ? aiTotals.promptTokens
      : resolvedRunTotals?.promptTokens ?? 0;
  const aiCompletionTokens =
    aiTotals.completionTokens > 0
      ? aiTotals.completionTokens
      : resolvedRunTotals?.completionTokens ?? 0;
  const tokensText =
    aiTokens != null && aiTokens > 0
      ? `${formatNumber(aiTokens)} tokens`
      : "No tokens recorded";

  const flowDescendants = primitive?.primitives?.descendants ?? [];
  const evidenceGroups = useMemo(
    () => groupPrimitivesByType(flowDescendants, OUTPUT_ENTITY_TYPES),
    [flowDescendants, refreshTick],
  );
  const totalSearchQueries = useMemo(() => {
    const all = [primitive, ...flowDescendants].filter(Boolean);
    const unique = new Map();
    all.forEach((item) => {
      if (item?.id && !unique.has(item.id)) {
        unique.set(item.id, item);
      }
    });
    return Array.from(unique.values()).reduce(
      (sum, item) => sum + resolvePrimitiveSearchQueryCount(item),
      0,
    );
  }, [primitive, flowDescendants, refreshTick]);
  const totalUrlsExamined = useMemo(() => {
    const all = [primitive, ...flowDescendants].filter(Boolean);
    const unique = new Map();
    all.forEach((item) => {
      if (item?.id && !unique.has(item.id)) {
        unique.set(item.id, item);
      }
    });
    return Array.from(unique.values()).reduce(
      (sum, item) => sum + resolvePrimitiveUrlExaminedCount(item),
      0,
    );
  }, [primitive, flowDescendants, refreshTick]);

  const autoHeroPhase = heroRunningPhases[0] ?? runningPhase ?? null;
  const selectedLivePhase = useMemo(() => {
    if (!selectedLiveStepId) {
      return null;
    }
    return (
      phaseDetails.find((phase) => `${phase?.id}` === `${selectedLiveStepId}`) ?? null
    );
  }, [phaseDetails, selectedLiveStepId]);
  const heroPhase = selectedLivePhase ?? autoHeroPhase;
  const pausedHeroEntry = heroPhase?.pausedEntry ?? null;
  const canResumePausedStep = Boolean(
    primitive?.id && pausedHeroEntry?.id && pausedHeroEntry?.needReason === "awaiting_confirmation",
  );
  const pausedVerificationViews = useMemo(() => {
    const stepPrimitive = pausedHeroEntry?.primitive;
    return collectPausedVerificationViews(stepPrimitive);
  }, [pausedHeroEntry, refreshTick]);
  const showPausedVerificationPanel =
    !isFlowComplete &&
    heroPhase?.status === "paused" &&
    pausedHeroEntry?.needReason === "awaiting_confirmation" &&
    pausedVerificationViews.length > 0;
  const resumePausedStep = useCallback(async () => {
    if (!canResumePausedStep) {
      return;
    }
    try {
      await mainstore.doPrimitiveAction(primitive, "resume_paused_step", {
        stepId: pausedHeroEntry.id,
      });
      forceRefresh();
    } catch (error) {
      console.error("Failed to resume paused step", error);
    }
  }, [canResumePausedStep, mainstore, pausedHeroEntry?.id, primitive]);
  const hasMultipleRunning = !selectedLivePhase && heroRunningPhases.length > 1;
  const heroPercent = safePercent(heroPhase?.percentage);
  const runningPhaseSwarm = useMemo(() => {
    return heroRunningPhases.map((phase) => {
      const avatarUrl = phase.primitives
        ?.map((item) => item?.avatarImageUrl)
        .find((url) => typeof url === "string" && url.trim().length > 0);
      return {
        id: phase.id,
        label: phase.name,
        avatarUrl,
        icon: phase.icon,
        percent: safePercent(phase.percentage),
        message: phase.message,
        primitives: phase.primitives,
      };
    });
  }, [heroRunningPhases]);
  const heroProcessingPrimitive = useMemo(() => {
    return heroPhase?.primitives?.find((item) =>
      PROCESSING_STEP_TYPES.has(item?.type),
    );
  }, [heroPhase?.primitives]);
  const showHeroEvidenceRing = Boolean(
    heroProcessingPrimitive &&
      ["query", "summary", "categorizer"].includes(heroProcessingPrimitive.type),
  );
  const resolveEvidenceItems = useCallback(
    (primitives = [], options = {}) => {
      const allowGlobalFallback = options.allowGlobalFallback !== false;
      const filtered = primitives.filter((item) =>
        OUTPUT_ENTITY_TYPES.has(item?.type),
      );
      const base = filtered.length ? filtered : primitives;
      const withImages = base.filter((item) => item?.avatarImageUrl);
      if (withImages.length > 0) {
        return withImages.slice(0, 6);
      }
      if (!allowGlobalFallback) {
        return base.filter(Boolean).slice(0, 6);
      }
      const fallback = flowDescendants.filter(
        (item) => OUTPUT_ENTITY_TYPES.has(item?.type) && item?.avatarImageUrl,
      );
      return fallback.slice(0, 6);
    },
    [flowDescendants],
  );
  const runningPhaseFeedView = useMemo(() => {
    return runningPhaseSwarm.slice(0, 3);
  }, [runningPhaseSwarm]);
  const activeHeroPhase = hasMultipleRunning
    ? runningPhaseSwarm[highlightedRunningIndex % Math.max(1, runningPhaseSwarm.length)] ??
      heroPhase
    : heroPhase;
  const activeEvidenceItems = useMemo(() => {
    return resolveEvidenceItems(activeHeroPhase?.primitives ?? [], {
      allowGlobalFallback: false,
    });
  }, [activeHeroPhase?.primitives, resolveEvidenceItems]);
  const activeProcessingPrimitive = useMemo(() => {
    return activeHeroPhase?.primitives?.find((item) =>
      PROCESSING_STEP_TYPES.has(item?.type),
    );
  }, [activeHeroPhase?.primitives]);
  const showActiveEvidenceRing = Boolean(
    activeProcessingPrimitive &&
      ["query", "summary", "categorizer"].includes(activeProcessingPrimitive.type),
  );

  useEffect(() => {
    if (!selectedLiveStepId) {
      return;
    }
    const stillExists = phaseDetails.some((phase) => `${phase?.id}` === `${selectedLiveStepId}`);
    if (!stillExists) {
      setSelectedLiveStepId(undefined);
    }
  }, [phaseDetails, selectedLiveStepId]);

  useEffect(() => {
    if (!hasMultipleRunning) {
      setHighlightedRunningIndex(0);
    }
  }, [hasMultipleRunning]);

  useEffect(() => {
    if (!hasMultipleRunning || runningPhaseSwarm.length < 2) {
      setHighlightedRunningIndex(0);
      return undefined;
    }
    const interval = setInterval(() => {
      setHighlightedRunningIndex((idx) => (idx + 1) % runningPhaseSwarm.length);
    }, 4200);
    return () => clearInterval(interval);
  }, [hasMultipleRunning, runningPhaseSwarm.length]);

  useEffect(() => {
    const container = heroProgressLogRef.current;
    if (!container) {
      return;
    }
    const scrollTarget =
      container.querySelector?.("[data-slot='viewport']") ??
      container.querySelector?.("[data-slot='wrapper']") ??
      container;
    if (!scrollTarget || typeof scrollTarget.scrollTop !== "number") {
      return;
    }
    const raf = requestAnimationFrame(() => {
      scrollTarget.scrollTop = scrollTarget.scrollHeight;
    });
    return () => cancelAnimationFrame(raf);
  }, [heroPhase?.id, heroProgressHistory.length]);

  const heroMetricSummary = useMemo(() => {
    const summary = {
      analyzed: 0,
      kept: 0,
      max: 0,
      processed: 0,
    };
    (heroPhase?.metrics ?? []).forEach((metric) => {
      if (metric.type === "searchStats") {
        summary.analyzed += metric.scannedValue ?? 0;
        summary.kept += metric.keptValue ?? 0;
        if (metric.maxValue != null) {
          summary.max += metric.maxValue;
        }
        return;
      }
      if (metric.type === "simple") {
        if (metric.currentValue != null) {
          summary.processed += metric.currentValue;
        }
        if (metric.maxValue != null) {
          summary.max += metric.maxValue;
        }
      }
    });
    return summary;
  }, [heroPhase?.metrics]);

  const heroProgressData = useMemo(() => {
    const candidates = [];
    const seenProgressPrimitiveIds = new Set();
    const phasePrimitives = heroPhase?.primitives ?? [];
    phasePrimitives.forEach((item) => {
      appendPrimitiveProgressCandidates(item, candidates, seenProgressPrimitiveIds);
    });
    const processing = heroProcessingPrimitive?.processing;
    if (processing && typeof processing === "object") {
      Object.values(processing).forEach((value) => {
        if (value && typeof value === "object" && value.progress !== undefined) {
          candidates.push(value.progress);
        }
      });
    }
    if (heroPhase?.message) {
      candidates.push(heroPhase.message);
    }
    const messages = new Set();
    const urlEntries = new Map();
    const queries = new Set();
    const phases = new Set();
    const percents = [];
    candidates.forEach((candidate) => {
      getProgressMessages(candidate, messages);
      collectUrlEntriesFromValue(candidate, urlEntries);
      getProgressQueries(candidate, queries);
      getProgressPhases(candidate, phases);
      const pct = extractPercentFromProgressValue(candidate);
      if (typeof pct === "number") {
        percents.push(pct);
      }
    });
    const orderedMessages = Array.from(messages).filter(
      (message) => !isGenericStatusOnlyMessage(message),
    );
    const fallbackPhaseMessage = isGenericStatusOnlyMessage(heroPhase?.message)
      ? undefined
      : heroPhase?.message;
    const rawCurrentMessage = orderedMessages[0] ?? fallbackPhaseMessage ?? heroPhase?.name ?? undefined;
    const parsedSearchMessage = parseSearchQueryMessage(rawCurrentMessage);
    const lowSignal = isGenericPercentMessage(rawCurrentMessage);
    const currentMessage = parsedSearchMessage
      ? buildFallbackLiveMessage(heroPhase?.name, heroPhase?.status, heroPhase?.percentage)
      : lowSignal
      ? buildFallbackLiveMessage(heroPhase?.name, heroPhase?.status, heroPhase?.percentage)
      : rawCurrentMessage;
    return {
      currentMessage,
      rawCurrentMessage,
      lowSignal,
      derivedPercent: percents.length ? Math.max(...percents) : undefined,
      activeQuery: parsedSearchMessage?.query ?? Array.from(queries)[0] ?? undefined,
      activePhase: Array.from(phases)[0] ?? undefined,
      urls: Array.from(urlEntries.values()),
    };
  }, [
    heroPhase?.primitives,
    heroProcessingPrimitive,
    heroPhase?.message,
    heroPhase?.name,
    heroPhase?.status,
    heroPhase?.percentage,
    refreshTick,
  ]);
  const messagePercent = extractPercentFromMessage(heroProgressData.rawCurrentMessage);
  const heroPercentDisplay =
    typeof heroPercent === "number" && Number.isFinite(heroPercent)
      ? heroPercent
      : typeof heroProgressData.derivedPercent === "number" &&
        Number.isFinite(heroProgressData.derivedPercent)
      ? heroProgressData.derivedPercent
      : messagePercent;

  useEffect(() => {
    const phaseId = heroPhase?.id;
    if (!phaseId) {
      setHeroProgressHistory([]);
      return;
    }
    const map = progressHistoryRef.current;
    const existing = map.get(phaseId) ?? [];
    const nextMessage =
      typeof heroProgressData.currentMessage === "string"
        ? heroProgressData.currentMessage.trim()
        : "";
    if (!nextMessage) {
      setHeroProgressHistory(existing);
      return;
    }
    if (isLifecycleStatusLogMessage(nextMessage)) {
      setHeroProgressHistory(existing);
      return;
    }
    const latest = existing[existing.length - 1];
    const urlKey = heroProgressData.urls.map((entry) => entry?.url).filter(Boolean).join("|");
    const shouldMergeLatest =
      Boolean(latest) &&
      latest.urlKey === urlKey &&
      shouldMergeHistoryProgressMessage(latest.message, nextMessage);
    if (shouldMergeLatest) {
      const updated = existing.slice(0, -1).concat({
        ...latest,
        message: nextMessage,
        urls: heroProgressData.urls.slice(0, 12),
        urlKey,
      });
      map.set(phaseId, updated);
      setHeroProgressHistory(updated);
      return;
    }
    if (latest && latest.message === nextMessage && latest.urlKey !== urlKey) {
      const updated = existing.slice(0, -1).concat({
        ...latest,
        urls: heroProgressData.urls.slice(0, 12),
        urlKey,
      });
      map.set(phaseId, updated);
      setHeroProgressHistory(updated);
      return;
    }
    const shouldAppend = !latest || latest.message !== nextMessage;
    if (shouldAppend) {
      const updated = [
        ...existing,
        {
          id: `${phaseId}_${Date.now()}`,
          message: nextMessage,
          urls: heroProgressData.urls.slice(0, 12),
          urlKey,
        },
      ].slice(-60);
      map.set(phaseId, updated);
      setHeroProgressHistory(updated);
      return;
    }
    setHeroProgressHistory(existing);
  }, [heroPhase?.id, heroProgressData.currentMessage, heroProgressData.urls]);

  useEffect(() => {
    const phaseId = heroPhase?.id;
    if (!phaseId) {
      setHeroStageHistory([]);
      return;
    }
    const map = stageHistoryRef.current;
    const existing = map.get(phaseId) ?? [];
    const nextPhase =
      typeof heroProgressData.activePhase === "string"
        ? heroProgressData.activePhase.trim()
        : "";
    if (!nextPhase) {
      setHeroStageHistory(existing);
      return;
    }
    if ((existing[existing.length - 1] ?? "").toLowerCase() === nextPhase.toLowerCase()) {
      setHeroStageHistory(existing);
      return;
    }
    const updated = [...existing, nextPhase].slice(-20);
    map.set(phaseId, updated);
    setHeroStageHistory(updated);
  }, [heroPhase?.id, heroProgressData.activePhase]);

  const heroProgressLinks = useMemo(() => {
    const phasePrimitives = Array.isArray(heroPhase?.primitives) ? heroPhase.primitives : [];
    const searchPrimitives = phasePrimitives.filter((item) => item?.type === "search");
    if (searchPrimitives.length === 0) {
      return [];
    }
    const uniqueResultPrimitives = new Map();
    searchPrimitives.forEach((searchPrimitive) => {
      const fromResults = toPrimitiveItems(searchPrimitive?.primitives?.results);
      const fromOrigin = toPrimitiveItems(searchPrimitive?.primitives?.origin);
      const fromDescendants = toPrimitiveItems(searchPrimitive?.primitives?.descendants);
      [...fromResults, ...fromOrigin, ...fromDescendants]
        .filter((item) => OUTPUT_ENTITY_TYPES.has(item?.type))
        .forEach((item) => {
          const key = `${item?.id ?? item?._id ?? ""}`;
          if (!key) {
            return;
          }
          if (!uniqueResultPrimitives.has(key)) {
            uniqueResultPrimitives.set(key, item);
          }
        });
    });
    const linksByUrl = new Map();
    Array.from(uniqueResultPrimitives.values()).forEach((item) => {
      const url = resolveResultPrimitiveUrl(item);
      if (!url) {
        return;
      }
      const image = resolveResultPrimitiveImage(item);
      const title =
        (typeof item?.title === "string" && item.title.trim()) ||
        (typeof item?.referenceParameters?.title === "string" && item.referenceParameters.title.trim()) ||
        (typeof item?.referenceParameters?.name === "string" && item.referenceParameters.name.trim()) ||
        null;
      if (!linksByUrl.has(url)) {
        linksByUrl.set(url, {
          url,
          domain: getDomainFromUrl(url) ?? "link",
          image: image ?? null,
          title,
        });
        return;
      }
      if (image && !linksByUrl.get(url)?.image) {
        linksByUrl.set(url, {
          ...linksByUrl.get(url),
          image,
        });
      }
    });
    return Array.from(linksByUrl.values()).slice(0, 18);
  }, [heroPhase?.id, heroPhase?.primitives, refreshTick]);
  const heroProgressGalleryColumnCount = heroProgressLinks.length <= 1 ? 1 : 2;
  const heroProgressGalleryCycleLinks = useMemo(() => {
    const cycle = [...heroProgressLinks];
    if (heroProgressGalleryColumnCount > 1 && cycle.length > 1 && cycle.length % heroProgressGalleryColumnCount !== 0) {
      cycle.push({
        __spacer: true,
        key: `spacer_${cycle.length}`,
      });
    }
    return cycle;
  }, [heroProgressLinks, heroProgressGalleryColumnCount]);
  const heroProgressGalleryLinks = useMemo(() => {
    if (heroProgressGalleryCycleLinks.length > 1) {
      return heroProgressGalleryCycleLinks.concat(heroProgressGalleryCycleLinks);
    }
    return heroProgressGalleryCycleLinks;
  }, [heroProgressGalleryCycleLinks]);
  const heroProgressGalleryRowCount = Math.ceil(
    heroProgressGalleryCycleLinks.length / Math.max(1, heroProgressGalleryColumnCount),
  );
  useEffect(() => {
    const viewport = heroUrlGalleryViewportRef.current;
    const track = heroUrlGalleryRef.current;
    if (!viewport || !track) {
      return;
    }
    if (heroProgressLinks.length < 2 || heroProgressGalleryRowCount <= 1) {
      viewport.scrollTop = 0;
      return;
    }
    let timerId = null;
    let stopped = false;
    const settleMs = 4600;
    const pauseMs = 192;
    const yOffset = 5;
    const singleLoopHeightFallback = Math.max(1, (track.scrollHeight || 0) / 2);
    const children = Array.from(track.children ?? []);
    const firstCycleChildren = children.slice(0, Math.max(1, Math.floor(children.length / 2)));
    const rowTops = Array.from(
      new Set(
        firstCycleChildren
          .map((node) => Number(node?.offsetTop ?? 0))
          .filter((value) => Number.isFinite(value)),
      ),
    ).sort((a, b) => a - b);
    if (rowTops.length <= 1) {
      viewport.scrollTo({ top: yOffset, behavior: "auto" });
      return;
    }
    const rowStep = rowTops.length >= 2 ? Math.max(1, rowTops[1] - rowTops[0]) : 0;
    const singleLoopHeight = rowStep > 0
      ? rowStep * heroProgressGalleryRowCount
      : singleLoopHeightFallback;

    let currentRowIndex = 0;

    const scheduleStep = (delay) => {
      if (stopped) {
        return;
      }
      timerId = window.setTimeout(() => {
        if (stopped) {
          return;
        }
        const nextRowIndex = (currentRowIndex + 1) % rowTops.length;
        const wrapsToStart = nextRowIndex === 0;
        const targetTop = wrapsToStart
          ? rowTops[0] + singleLoopHeight + yOffset
          : rowTops[nextRowIndex] + yOffset;

        viewport.scrollTo({ top: targetTop, behavior: "smooth" });

        timerId = window.setTimeout(() => {
          if (stopped) {
            return;
          }
          if (wrapsToStart) {
            viewport.scrollTo({ top: rowTops[0] + yOffset, behavior: "auto" });
          }
          currentRowIndex = nextRowIndex;
          scheduleStep(pauseMs);
        }, settleMs);
      }, delay);
    };

    viewport.scrollTo({ top: rowTops[0] + yOffset, behavior: "auto" });
    scheduleStep(settleMs);
    return () => {
      stopped = true;
      if (timerId) {
        window.clearTimeout(timerId);
      }
    };
  }, [heroProgressGalleryLinks, heroPhase?.id, heroProgressGalleryRowCount]);
  const compactHeroProgressHistory = useMemo(() => {
    return coalesceActionLogHistory(
      heroProgressHistory
      .filter((entry) => !isLifecycleStatusLogMessage(entry?.message))
      .filter((entry) => !isDuplicateProgressLine(entry?.message, heroPhase?.name))
      .filter((entry) =>
        !isDuplicateAgainstHeadline(entry?.message, heroProgressData.currentMessage, heroProgressData.activeQuery),
      )
      .slice(-30),
    );
  }, [heroProgressHistory, heroPhase?.name, heroProgressData.currentMessage, heroProgressData.activeQuery]);
  const shouldShowHeroProgressLog =
    compactHeroProgressHistory.length > 1 ||
    (compactHeroProgressHistory.length === 1 &&
      !isGenericPercentMessage(compactHeroProgressHistory[0]?.message ?? ""));
  const heroPhaseBadges = useMemo(() => {
    const unique = [];
    const seen = new Set();
    heroStageHistory.forEach((stage) => {
      const key = String(stage ?? "").trim().toLowerCase();
      if (!key || seen.has(key)) {
        return;
      }
      seen.add(key);
      unique.push(stage);
    });
    if (unique.length < 2) {
      return [];
    }
    const currentKey = String(unique[unique.length - 1] ?? "").trim().toLowerCase();
    return unique.map((stage) => {
      const key = String(stage ?? "").trim().toLowerCase();
      const isCurrent = key === currentKey;
      return {
        key,
        label: toPhaseLabel(stage),
        isCurrent,
        isComplete: !isCurrent,
      };
    });
  }, [heroStageHistory]);
  const heroPhaseBadgeDisplay = useMemo(() => {
    if (!heroPhaseBadges.length) {
      return { visible: [], hiddenPrevious: 0 };
    }
    const previous = heroPhaseBadges.filter((badge) => !badge.isCurrent);
    const current = heroPhaseBadges.find((badge) => badge.isCurrent);
    const hiddenPrevious = Math.max(0, previous.length - 5);
    const visiblePrevious =
      hiddenPrevious > 0 ? previous.slice(previous.length - 5) : previous;
    return {
      visible: current ? [...visiblePrevious, current] : visiblePrevious,
      hiddenPrevious,
    };
  }, [heroPhaseBadges]);

  const heroEvidenceItems = useMemo(() => {
    if (!heroPhase?.primitives) {
      return [];
    }
    const filtered = heroPhase.primitives.filter((item) =>
      OUTPUT_ENTITY_TYPES.has(item?.type),
    );
    const base = filtered.length ? filtered : heroPhase.primitives;
    return base.filter(Boolean).slice(0, 6);
  }, [heroPhase?.primitives]);

  const checklistSteps = useMemo(() => {
    return phaseDetails.map((phase) => {
      const status = phase.status ?? phase.rawStatus ?? "pending";
      const normalized = String(status).toLowerCase();
      const resolved =
        isFlowComplete && !["error", "failed"].includes(normalized)
          ? "complete"
          : normalized;
      let color = "bg-slate-300";
      if (resolved === "running") {
        color = "bg-blue-500";
      } else if (resolved === "paused") {
        color = "bg-amber-500";
      } else if (resolved === "complete") {
        color = "bg-emerald-500";
      } else if (resolved === "error" || resolved === "failed") {
        color = "bg-rose-500";
      } else if (resolved === "waiting" || resolved === "queued") {
        color = "bg-amber-400";
      }
      let stepMessage = phase.message;
      if (
        ["paused", "waiting", "queued", "pending", "not_run", "not_complete", "scaffolding"].includes(resolved) &&
        isZeroSearchMetricsMessage(stepMessage)
      ) {
        stepMessage = undefined;
      }
      return {
        id: phase.id,
        name: phase.name,
        status: resolved,
        color,
        message: stepMessage,
      };
    });
  }, [phaseDetails, isFlowComplete]);

  const timelineSteps = useMemo(() => {
    if (showAllSteps || checklistSteps.length <= 10) {
      return checklistSteps.map((step, idx) => ({ ...step, kind: "step", idx }));
    }
    const currentIdx = checklistSteps.findIndex(
      (step) => step.status === "running",
    );
    const fallbackIdx = currentIdx >= 0
      ? currentIdx
      : checklistSteps.findIndex((step) => step.status === "paused" || step.status === "waiting");
    const activeIdx = fallbackIdx >= 0 ? fallbackIdx : 0;
    const lastIdx = checklistSteps.length - 1;
    const firstIdx = 0;
    const prevIdx = Math.max(activeIdx - 1, 0);
    const nextIdx = Math.min(activeIdx + 1, lastIdx);
    const anchorIds = new Set([firstIdx, prevIdx, activeIdx, nextIdx, lastIdx]);
    const anchors = Array.from(anchorIds)
      .sort((a, b) => a - b)
      .map((idx) => ({ ...checklistSteps[idx], kind: "step", idx }));
    const output = [];
    let lastIncluded = -1;
    for (const anchor of anchors) {
      if (anchor.idx - lastIncluded > 1) {
        const hiddenCount = anchor.idx - lastIncluded - 1;
        output.push({
          id: `collapsed_${lastIncluded}_${anchor.idx}`,
          kind: "collapsed",
          count: hiddenCount,
        });
      }
      output.push(anchor);
      lastIncluded = anchor.idx;
    }
    return output;
  }, [checklistSteps, showAllSteps]);

  if (!primitive) {
    return null;
  }

  return (
    <div
      className={clsx(
        "rounded-xl border border-slate-200 bg-white p-6 shadow-sm",
        className,
        variant === "popup" ? "max-w-3xl" : "",
      )}
    >
      <div className="flex flex-col space-y-2">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-500">
              Workflow Progress
            </p>
            <h2 className="text-lg font-semibold text-slate-900">
              {primitive.origin?.title ?? primitive.title}
            </h2>
          </div>
	          {showControls && (
	            <div className="flex gap-3">
	              <Tooltip content="Pause support coming soon">
	                <Button
	                  size="sm"
	                  variant="flat"
	                  color="warning"
	                  startContent={<PauseIcon className="h-4 w-4" />}
	                  isDisabled
	                >
	                  Pause
	                </Button>
	              </Tooltip>
	              <Tooltip content="Requires workflow stop handler">
	                <Button
	                  size="sm"
	                  variant="bordered"
	                  color="danger"
	                  startContent={<StopIcon className="h-4 w-4" />}
	                  isDisabled
	                >
	                  Stop
	                </Button>
	              </Tooltip>
	            </div>
	          )}
	        </div>
          <dl className="mt-2 flex flex-wrap gap-4 text-xs text-slate-600 grid grid-cols-2 lg:grid-cols-4 4xl:grid-cols-7">
            {startedAt && (
              <div>
                <dt className="font-medium text-slate-500">Started</dt>
                <dd>{startedAt}</dd>
              </div>
            )}
            {elapsed && (
              <div>
                <dt className="font-medium text-slate-500">
                  {completedAt ? "Elapsed" : "Running For"}
                </dt>
                <dd>{elapsed}</dd>
              </div>
            )}
            <div>
              <dt className="font-medium text-slate-500">Current Steps</dt>
              <dd>{trackedCount}</dd>
            </div>
            <div>
              <dt className="font-medium text-slate-500">Search Queries</dt>
              <dd>{formatNumber(totalSearchQueries ?? 0)}</dd>
            </div>
            <div>
              <dt className="font-medium text-slate-500">URLs Examined</dt>
              <dd>{formatNumber(totalUrlsExamined ?? 0)}</dd>
            </div>
            {completedAt && (
              <div>
                <dt className="font-medium text-slate-500">Completed</dt>
                <dd>{completedAt}</dd>
              </div>
            )}
            {eta && (
              <div>
                <dt className="font-medium text-slate-500">ETA</dt>
                <dd>{eta}</dd>
              </div>
            )}
            {showCost && totalRunCost != null && (
              <div>
                <dt className="font-medium text-slate-500">Total Cost</dt>
                <dd className="flex flex-col">
                  <span>{formatCurrency(totalRunCost)}</span>
                </dd>
              </div>
            )}
            {showCost && aiCost > 0 && (
              <div>
                <dt className="font-medium text-slate-500">AI Cost</dt>
                <dd className="flex flex-col">
                  <span>{formatCurrency(aiCost)}</span>
                  <span className="text-[10px] uppercase tracking-wide text-slate-400">
                    {tokensText}
                  </span>
                </dd>
              </div>
            )}
            {showCost &&  <div>
              <dt className="font-medium text-slate-500">
                Data
              </dt>
              <dd className="flex flex-col">
                <span>
                  {costSnapshot ? (externalCost > 0 ? formatCurrency(externalCost) : `${formatNumber(externalTotals.totalTokens)} operations`) : "-"}
                </span>
                  <span className="text-[10px] uppercase tracking-wide text-slate-400">                    
                    {costSnapshot ? `${externalCost > 0 ? `${formatNumber(externalTotals.totalTokens)}} operations · ` : ""}${formatNumber(externalTotals.records)} entries` : "-" }
                  </span>
              </dd>
            </div>}
            {costError && (
              <div>
                <dt className="font-medium text-slate-500">Cost Tracking</dt>
                <dd className="text-[11px] text-rose-500">
                  Unable to load cost ({costError.message})
                </dd>
              </div>
            )}
          </dl>
      </div>

      <div className="mt-6 space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-slate-700">Overall</span>
          <span className="text-sm font-semibold text-slate-800">
            {percentComplete}%
          </span>
        </div>
        <Progress
          value={percentComplete}
          aria-label="Overall progress"
          classNames={{ indicator: "bg-slate-800" }}
        />
        <p className="text-xs text-slate-500">
          {completedCount} completed · {runningCount} running
          {failedCount ? ` · ${failedCount} failed` : ""} · {trackedCount} tracked
        </p>
        {progressMessage && (
          <p className="text-xs text-slate-500">{progressMessage}</p>
        )}
      </div>

      <section className="mt-8 space-y-6">
        <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-600">
                Live step
              </h3>
              <div className="flex items-center gap-2">
                {selectedLivePhase && (
                  <button
                    type="button"
                    onClick={() => setSelectedLiveStepId(undefined)}
                    className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-600 transition hover:border-slate-300 hover:text-slate-800"
                  >
                    Follow live
                  </button>
                )}
                <span className="text-xs font-medium text-slate-500">
                  {selectedLivePhase
                    ? "Pinned from timeline"
                    : hasMultipleRunning
                    ? `${runningPhases.length} running`
                    : heroPhase?.status === "paused"
                    ? "Paused"
                    : heroPhase?.status === "running"
                    ? "Running"
                    : heroPhase?.status === "waiting"
                    ? "Waiting"
                    : heroPhase?.status === "scaffolding"
                    ? "Preparing"
                    : heroPhase
                    ? "Pending"
                    : "Idle"}
                </span>
              </div>
            </div>
            <AnimatePresence initial={false}>
              {heroPhase ? (
                <motion.div
                  key={heroPhase.id}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.2 }}
                  className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm min-h-[150px]"
                >
                  {hasMultipleRunning ? (
                    <div className="grid gap-4 md:min-h-[220px] md:grid-cols-[minmax(0,1fr)_220px] md:items-start">
                      <div className="min-h-[140px] md:min-h-[220px]">
                        <AnimatePresence mode="wait" initial={false}>
                          <motion.div
                            key={`active_multi_${activeHeroPhase?.id ?? "none"}`}
                            initial={{ opacity: 0, y: 14 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -14 }}
                            transition={{ duration: 0.22 }}
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <div className="flex items-center gap-2">
                                  {activeHeroPhase?.icon && (
                                    <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-slate-600">
                                      <HeroIcon icon={activeHeroPhase.icon} className="h-4 w-4" />
                                    </span>
                                  )}
                                  <p className="text-base font-semibold text-slate-900">
                                    {activeHeroPhase?.label ?? activeHeroPhase?.name}
                                  </p>
                                </div>
                                {activeHeroPhase?.message && (
                                  <p className="text-xs text-slate-500">
                                    {activeHeroPhase.message}
                                  </p>
                                )}
                              </div>
                              {typeof activeHeroPhase?.percent === "number" && (
                                activeHeroPhase.percent > 0 ? (
                                  <div className="rounded-full bg-slate-900 px-3 py-1 text-xs font-semibold text-white">
                                    {Math.round(activeHeroPhase.percent)}%
                                  </div>
                                ) : (
                                  <div className="rounded-full bg-slate-900 px-3 py-1 text-xs font-semibold text-white">
                                    <span className="inline-block h-3 w-3 rounded-full border-2 border-current border-t-transparent align-middle animate-spin" />
                                  </div>
                                )
                              )}
                            </div>
                            {(activeEvidenceItems.length > 0 || activeHeroPhase?.primitives?.length) && (
                              <div className="mt-4 flex items-center">
                                {showActiveEvidenceRing ? (
                                  <ProgressRing percent={activeHeroPhase?.percent} size={40} showLabel={false} />
                                ) : (
                                  activeEvidenceItems.map((item, idx) => (
                                    <div
                                      key={item.id ?? item._id ?? idx}
                                      className="relative"
                                      style={{ marginLeft: idx === 0 ? 0 : "-0.4rem" }}
                                    >
                                      <EvidenceAvatar
                                        item={item}
                                        className="h-10 w-10 text-xs font-semibold text-slate-600 ring-2 ring-white"
                                      />
                                    </div>
                                  ))
                                )}
                              </div>
                            )}
                            {typeof activeHeroPhase?.percent === "number" && (
                              <div className="mt-4">
                                <Progress
                                  value={activeHeroPhase.percent}
                                  aria-label="Highlighted progress"
                                  classNames={{ indicator: "bg-slate-900" }}
                                />
                              </div>
                            )}
                          </motion.div>
                        </AnimatePresence>
                      </div>
                      <div className="border-l border-slate-200 pl-4 md:min-h-[220px]">
                        <p className="text-xxs font-semibold uppercase tracking-wide text-slate-500 pb-2">
                          Multiple steps in progress
                        </p>
                        <div className="max-h-[180px] overflow-y-auto pr-1">
                          <div className="flex flex-col gap-2">
                            {runningPhaseFeedView.map((entry) => (
                              <div
                                key={`swarm_feed_${entry?.id ?? entry?.label}`}
                                className={clsx(
                                  "flex items-center gap-2 rounded-lg border px-3 py-2 text-left text-xs",
                                  entry?.id === activeHeroPhase?.id
                                    ? accentBorderClassName ?? "border-blue-400"
                                    : "border-slate-200",
                                )}
                              >
                                {entry?.icon && (
                                  <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-slate-100 text-slate-600">
                                    <HeroIcon icon={entry.icon} className="h-4 w-4" />
                                  </span>
                                )}
                                <span className="truncate font-semibold text-slate-900">
                                  {entry.label}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <div className="flex items-center gap-2">
                            {heroPhase?.icon ? (
                              <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-slate-600">
                                <HeroIcon icon={heroPhase.icon} className="h-5 w-5" />
                              </span>
                            ) : null}
                            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                              {heroPhase.name}
                            </p>
                          </div>
                          <p className="mt-2 text-2xl font-semibold text-slate-900">
                            {heroProgressData.currentMessage ?? "Running workflow step"}
                          </p>
                          {heroProgressData.activeQuery && (
                            <p className="mt-1 text-xs text-slate-500">
                              Search query: "{heroProgressData.activeQuery}"
                            </p>
                          )}
                        </div>
                        {(heroPhaseBadges.length > 0 || heroPercentDisplay != null) && (
                          <div className="flex items-center gap-2">
                            {heroPhaseBadges.length > 0 ? (
                              <>
                                {heroPhaseBadgeDisplay.hiddenPrevious > 0 && (
                                  <span className="inline-flex items-center rounded-full border border-slate-300 bg-white px-2 py-1 text-[10px] font-semibold text-slate-600">
                                    +{heroPhaseBadgeDisplay.hiddenPrevious}
                                  </span>
                                )}
                                {heroPhaseBadgeDisplay.visible.map((badge) => (
                                  <span
                                    key={badge.key}
                                    className={clsx(
                                      "inline-flex items-center gap-1.5 rounded-full border px-2 py-1 text-xs font-semibold",
                                      badge.isCurrent
                                        ? "border-slate-900 bg-slate-900 text-white"
                                        : "border-slate-300 bg-white text-slate-700",
                                    )}
                                    title={badge.label}
                                  >
                                    {badge.isCurrent ? (
                                      <span className="h-3 w-3 rounded-full border-2 border-current border-t-transparent animate-spin" />
                                    ) : (
                                      <CheckCircleIcon className="h-3.5 w-3.5" />
                                    )}
                                    {badge.isCurrent && heroPercentDisplay != null && heroPercentDisplay > 0 && (
                                      <span className="rounded-full bg-white/15 px-1.5 py-0.5 text-[10px] font-bold text-white leading-none">
                                        {Math.round(heroPercentDisplay)}%
                                      </span>
                                    )}
                                  </span>
                                ))}
                              </>
                            ) : heroPercentDisplay != null ? (
                              heroPercentDisplay > 0 ? (
                                <div className="rounded-full bg-slate-900 px-3 py-1 text-xs font-semibold text-white">
                                  {Math.round(heroPercentDisplay)}%
                                </div>
                              ) : (
                                <div className="rounded-full bg-slate-900 px-3 py-1 text-xs font-semibold text-white">
                                  <span className="inline-block h-3 w-3 rounded-full border-2 border-current border-t-transparent align-middle animate-spin" />
                                </div>
                              )
                            ) : null}
                          </div>
                        )}
                      </div>
                      {showPausedVerificationPanel && (
                        <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50/60 p-3">
                          <div className="flex items-center justify-between gap-3">
                            <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">
                              Manual verification
                            </p>
                            <span className="text-[11px] font-medium text-amber-700">
                              Waiting for confirmation
                            </span>
                          </div>
                          <p className="mt-1 text-xs text-amber-800">
                            Review and adjust paused-step outputs before resuming.
                          </p>
                          <div className="mt-3 space-y-3">
                            {pausedVerificationViews.map((view) => (
                              <div key={view.key}>
                                {view.description && (
                                  <p className="mb-2 text-xs text-amber-800">
                                    {view.description}
                                  </p>
                                )}
                                {view.content}
                              </div>
                            ))}
                          </div>
                          {canResumePausedStep && (
                            <div className="mt-4 flex justify-end border-t border-amber-200 pt-3">
                              <Button
                                size="md"
                                color="warning"
                                variant="solid"
                                className="w-full sm:w-auto min-w-[180px] font-semibold"
                                onPress={resumePausedStep}
                              >
                                Resume Step
                              </Button>
                            </div>
                          )}
                        </div>
                      )}
                      <div className="mt-5 flex flex-wrap items-center gap-4">
                        {showHeroEvidenceRing ? (
                          <ProgressRing percent={heroPercentDisplay} size={56} showLabel={false} />
                        ) : heroEvidenceItems.length > 0 && (
                          <div className="flex items-center">
                            {heroEvidenceItems.map((item, idx) => (
                              <motion.div
                                key={item.id ?? item._id ?? idx}
                                layout
                                className="relative"
                                style={{ marginLeft: idx === 0 ? 0 : "-0.5rem" }}
                              >
                                <EvidenceAvatar
                                  item={item}
                                  className="h-14 w-14 text-sm font-semibold text-slate-600 ring-2 ring-white"
                                />
                              </motion.div>
                            ))}
                          </div>
                        )}
                        {shouldShowHeroProgressLog && (
                          <div className="min-w-[260px] max-w-[420px] flex-1">
                            <p className="mb-1 text-xxs font-semibold uppercase tracking-wide text-slate-500">
                              Action log
                            </p>
                            <ScrollShadow
                              ref={heroProgressLogRef}
                              className="max-h-[110px] rounded-lg border border-slate-200 bg-slate-50/60 px-2.5 py-2 pr-1"
                              orientation="vertical"
                            >
                              <div className="space-y-1 text-xs text-slate-600">
                                {compactHeroProgressHistory.map((entry, idx, arr) => {
                                  const isCurrent = idx === arr.length - 1;
                                  return (
                                    <div
                                      key={entry.id}
                                      className={clsx(
                                        "truncate",
                                        isCurrent ? "font-semibold text-slate-800" : "text-slate-600",
                                      )}
                                    >
                                      {entry.message}
                                    </div>
                                  );
                                })}
                              </div>
                            </ScrollShadow>
                          </div>
                        )}
                        <div className="flex flex-wrap gap-2 text-xs font-semibold text-slate-600">
                          {heroMetricSummary.analyzed > 0 && (
                            <span className="rounded-full bg-white px-3 py-1.5 shadow-sm">
                              {formatNumber(heroMetricSummary.analyzed)} analyzed
                            </span>
                          )}
                          {heroMetricSummary.kept > 0 && (
                            <span className="rounded-full bg-white px-3 py-1.5 shadow-sm">
                              {formatNumber(heroMetricSummary.kept)} kept
                            </span>
                          )}
                          {heroMetricSummary.processed > 0 && (
                            <span className="rounded-full bg-white px-3 py-1.5 shadow-sm">
                              {formatNumber(heroMetricSummary.processed)} items processed
                            </span>
                          )}
                          {heroMetricSummary.max > 0 && (
                            <span className="rounded-full bg-white px-3 py-1.5 shadow-sm">
                              Target {formatNumber(heroMetricSummary.max)}
                            </span>
                          )}
                        </div>
                        {heroProgressLinks.length > 0 && (
                            <div className="w-full md:ml-auto md:w-[380px] md:self-stretch">
                            <div
                              ref={heroUrlGalleryViewportRef}
                              className="relative max-h-[150px] overflow-hidden rounded-lg border border-slate-200 bg-slate-50/50 p-2"
                            >
                              <div
                                ref={heroUrlGalleryRef}
                                className={clsx(
                                  "grid gap-1.5",
                                  heroProgressGalleryColumnCount === 1
                                    ? "grid-cols-1"
                                    : "grid-cols-2",
                                )}
                              >
                                {heroProgressGalleryLinks.map((link, idx) => {
                                  if (link?.__spacer) {
                                    return (
                                      <div
                                        key={`${link.key ?? "spacer"}_${idx}`}
                                        className="pointer-events-none block overflow-hidden rounded-md border border-transparent bg-transparent"
                                        aria-hidden="true"
                                      >
                                        <div className="relative aspect-[4/3] w-full" />
                                      </div>
                                    );
                                  }
                                  return (
                                  <a
                                    key={`${link.url}_${idx}`}
                                    href={link.url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="group block overflow-hidden rounded-md border border-slate-200 bg-white transition hover:border-slate-300"
                                    title={link.url}
                                  >
                                    <div className="relative aspect-[4/3] w-full">
                                      <img
                                        src={buildRemoteImageSrc(link.image) ?? buildPreviewImageSrc(link.url) ?? ""}
                                        alt={link.title ?? link.domain}
                                        className="h-full w-full object-cover transition group-hover:scale-[1.03]"
                                        loading="lazy"
                                        referrerPolicy="no-referrer"
                                        onError={(event) => {
                                          const fallback = buildPreviewImageSrc(link.url);
                                          if (!fallback || event.currentTarget.src.endsWith(fallback)) {
                                            return;
                                          }
                                          event.currentTarget.src = fallback;
                                        }}
                                      />
                                    </div>
                                  </a>
                                  );
                                })}
                              </div>
                              <div className="pointer-events-none absolute inset-x-0 top-0 h-5 bg-gradient-to-b from-slate-50/95 to-transparent" />
                              <div className="pointer-events-none absolute inset-x-0 bottom-0 h-5 bg-gradient-to-t from-slate-50/95 to-transparent" />
                            </div>
                          </div>
                        )}
                      </div>
                      {heroPercentDisplay != null && (
                        <div className="mt-4">
                          <Progress
                            value={heroPercentDisplay}
                            aria-label="Step progress"
                            classNames={{ indicator: "bg-slate-900" }}
                          />
                        </div>
                      )}
                      {heroPhase.subflows?.length > 0 && (
                        <p className="mt-3 text-xs font-medium text-slate-500">
                          {heroPhase.subflowCounts?.total ?? heroPhase.subflows.length} subflows ·{" "}
                          {(heroPhase.subflowCounts?.running ?? 0)} running ·{" "}
                          {(heroPhase.subflowCounts?.complete ?? 0)} complete
                        </p>
                      )}
                    </>
                  )}
                </motion.div>
              ) : FORCE_SWARM_TEST ? (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-6 py-8 text-sm text-slate-500"
                >
                  Loading test swarm…
                </motion.div>
              ) : (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-6 py-8 text-sm text-slate-500"
                >
                  All steps are complete. Review the evidence gallery below.
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-600">
              Evidence gallery
            </h3>
            <span className="text-xs text-slate-500">
              {evidenceGroups.length} categories
            </span>
          </div>
          {evidenceGroups.length > 0 ? (
            <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 min-[1420px]:grid-cols-3">
              {evidenceGroups.map((group, groupIdx) => {
                const previewItems = (group.items ?? []).slice(0, 6);
                const remaining = Math.max(0, (group.items?.length ?? 0) - previewItems.length);
                return (
                  <motion.div
                    key={`evidence_group_${group.title}`}
                    className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2, delay: Math.min(groupIdx * 0.04, 0.2) }}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-slate-800">
                        {group.items?.[0]?.metadata?.plurals ||
                          group.items?.[0]?.metadata?.plural ||
                          (group.title?.endsWith("s") ? group.title : `${group.title ?? ""}s`)}
                      </span>
                      <span className="text-xs font-semibold text-slate-500">
                        {group.count}
                      </span>
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-1 lg:flex-nowrap">
                      {previewItems.map((item, idx) => (
                        <div
                          key={item.id ?? item._id ?? idx}
                          className={clsx("relative", idx === 0 ? "ml-0" : "md:-ml-2")}
                        >
                          <EvidenceAvatar
                            item={item}
                            className="h-8 w-8 sm:h-9 sm:w-9 text-tiny font-semibold text-slate-600 shrink-0"
                          />
                        </div>
                      ))}
                      {remaining > 0 && (
                        <Avatar
                          isBordered
                          name={`+${remaining}`}
                          className={clsx(
                            "h-8 w-8 sm:h-9 sm:w-9 min-w-[32px] sm:min-w-[36px] bg-slate-100 text-tiny font-semibold text-slate-500 shrink-0",
                            previewItems.length === 0 ? "ml-0" : "md:-ml-2",
                          )}
                        />
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          ) : (
            <p className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-3 py-6 text-sm text-slate-500">
              No evidence collected yet.
            </p>
          )}
        </div>
      </section>

      <section className="mt-8 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-600">
            Steps timeline
          </h3>
          <div className="flex items-center gap-3 text-xs text-slate-500">
            <button
              type="button"
              onClick={() => setShowAllSteps((prev) => !prev)}
              className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-600 transition hover:border-slate-300 hover:text-slate-800"
            >
              {showAllSteps ? "Collapse steps" : "View all steps"}
            </button>
            <span>
              {completedPhases.length} complete · {upcomingPhases.length} pending
            </span>
          </div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
          <div className="flex flex-col gap-2">
            {timelineSteps.length > 0 ? (
              timelineSteps.map((entry, idx) => {
                if (entry.kind === "collapsed") {
                  return (
                    <div
                      key={`timeline_gap_${idx}`}
                      className="rounded-md border border-dashed border-slate-200 bg-white/70 px-3 py-2 text-xs text-slate-500"
                    >
                      {entry.count} steps hidden
                    </div>
                  );
                }
                const status = entry.status ?? "pending";
                const isCompleted = status === "complete";
                const isRunning = status === "running";
                const isPaused = status === "paused";
                const isWaiting = status === "waiting" || status === "queued";
                const isFailed = status === "error" || status === "failed";
                const statusLabel = isRunning
                  ? "Active"
                  : isPaused
                  ? "Paused"
                  : isWaiting
                  ? "Queued"
                  : isFailed
                  ? "Error"
                  : isCompleted
                  ? "Done"
                  : "Pending";
                const stepTitle =
                  typeof entry.idx === "number"
                    ? `Step ${entry.idx + 1} · ${entry.name}`
                    : entry.name;
                const stepMeta = [entry.startedAt, entry.duration].filter(Boolean).join(" · ");
                const isSelectedLiveStep =
                  selectedLiveStepId !== undefined &&
                  `${entry.id}` === `${selectedLiveStepId}`;
                const selectLiveStep = () => {
                  if (!entry?.id) {
                    return;
                  }
                  setSelectedLiveStepId((prev) =>
                    `${prev ?? ""}` === `${entry.id}` ? undefined : entry.id,
                  );
                };

                if (isCompleted) {
                  return (
                    <div
                      key={entry.id ?? `timeline_${idx}`}
                      role="button"
                      tabIndex={0}
                      onClick={selectLiveStep}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          selectLiveStep();
                        }
                      }}
                      className={clsx(
                        "flex items-center justify-between rounded-md border bg-white/80 px-3 py-2 text-slate-600 shadow-sm cursor-pointer",
                        isSelectedLiveStep
                          ? accentBorderClassName ?? "border-blue-400"
                          : "border-slate-100",
                      )}
                    >
                      <div className="flex items-center gap-2">
                        <CheckCircleIcon className="h-4 w-4 text-emerald-500" />
                        <div className="flex flex-col">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-sm font-medium text-slate-700">
                              {stepTitle}
                            </span>
                            {stepMeta && (
                              <span className="text-[11px] text-slate-400">
                                {stepMeta}
                              </span>
                            )}
                          </div>
                          {entry.message && (
                            <span className="text-xs text-slate-500">
                              {entry.message}
                            </span>
                          )}
                        </div>
                      </div>
                      <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                        {statusLabel}
                      </span>
                    </div>
                  );
                }

                if (isFailed) {
                  return (
                    <div
                      key={entry.id ?? `timeline_${idx}`}
                      role="button"
                      tabIndex={0}
                      onClick={selectLiveStep}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          selectLiveStep();
                        }
                      }}
                      className={clsx(
                        "flex items-center justify-between rounded-md border bg-rose-50 px-3 py-2 text-rose-700 cursor-pointer",
                        isSelectedLiveStep
                          ? accentBorderClassName ?? "border-blue-400"
                          : "border-rose-200",
                      )}
                    >
                      <div className="flex items-center gap-2">
                        <ExclamationTriangleIcon className="h-4 w-4" />
                        <div className="flex flex-col">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-sm font-semibold">
                              {stepTitle}
                            </span>
                            {stepMeta && (
                              <span className="text-[11px] text-rose-400">
                                {stepMeta}
                              </span>
                            )}
                          </div>
                          {entry.message && (
                            <span className="text-xs text-rose-600">
                              {entry.message}
                            </span>
                          )}
                        </div>
                      </div>
                      <span className="text-[11px] font-semibold uppercase tracking-wide text-rose-600">
                        {statusLabel}
                      </span>
                    </div>
                  );
                }

                if (isRunning) {
                  return (
                    <div
                      key={entry.id ?? `timeline_${idx}`}
                      role="button"
                      tabIndex={0}
                      onClick={selectLiveStep}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          selectLiveStep();
                        }
                      }}
                      className={clsx(
                        "rounded-md border bg-white px-3 py-2 shadow-sm cursor-pointer",
                        isSelectedLiveStep
                          ? accentBorderClassName ?? "border-blue-400"
                          : "border-slate-200",
                      )}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex flex-col gap-0.5">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-sm font-semibold text-slate-900">
                              {stepTitle}
                            </span>
                            {stepMeta && (
                              <span className="text-[11px] text-slate-400">
                                {stepMeta}
                              </span>
                            )}
                          </div>
                          {entry.message && (
                            <span className="text-xs text-slate-500">
                              {entry.message}
                            </span>
                          )}
                        </div>
                        <span className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-semibold text-blue-700">
                          {statusLabel}
                        </span>
                      </div>
                    </div>
                  );
                }

                if (isPaused) {
                  return (
                    <div
                      key={entry.id ?? `timeline_${idx}`}
                      role="button"
                      tabIndex={0}
                      onClick={selectLiveStep}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          selectLiveStep();
                        }
                      }}
                      className={clsx(
                        "flex items-center justify-between rounded-md border bg-amber-50 px-3 py-2 text-amber-800 cursor-pointer",
                        isSelectedLiveStep
                          ? accentBorderClassName ?? "border-blue-400"
                          : "border-amber-200",
                      )}
                    >
                      <div className="flex items-center gap-2">
                        <PauseIcon className="h-4 w-4 text-amber-700" />
                        <div className="flex flex-col">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-sm font-semibold">
                              {stepTitle}
                            </span>
                            {stepMeta && (
                              <span className="text-[11px] text-amber-600">
                                {stepMeta}
                              </span>
                            )}
                          </div>
                          <span className="text-xs text-amber-700">
                            Waiting for confirmation
                          </span>
                        </div>
                      </div>
                      <span className="text-[11px] font-semibold uppercase tracking-wide text-amber-700">
                        {statusLabel}
                      </span>
                    </div>
                  );
                }

                return (
                  <div
                    key={entry.id ?? `timeline_${idx}`}
                    role="button"
                    tabIndex={0}
                    onClick={selectLiveStep}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        selectLiveStep();
                      }
                    }}
                    className={clsx(
                      "flex items-center justify-between rounded-md border border-dashed px-3 py-2 text-slate-400 cursor-pointer",
                      isSelectedLiveStep
                        ? accentBorderClassName ?? "border-blue-400"
                        : "border-slate-200",
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <ClockIcon className="h-4 w-4 text-slate-400" />
                      <div className="flex flex-col">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-sm font-medium text-slate-500">
                            {stepTitle}
                          </span>
                          {stepMeta && (
                            <span className="text-[11px] text-slate-400">
                              {stepMeta}
                            </span>
                          )}
                        </div>
                        {entry.message && (
                          <span className="text-xs text-slate-400">
                            {entry.message}
                          </span>
                        )}
                      </div>
                    </div>
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                      {statusLabel}
                    </span>
                  </div>
                );
              })
            ) : (
              <div className="rounded-md border border-dashed border-slate-200 bg-white/70 px-3 py-2 text-center text-xs text-slate-500">
                No steps available yet.
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
